#!/usr/bin/env node
/**
 * okra CLI — Agent-friendly PDF extraction and collection queries.
 *
 * Global flags:
 *   -j, --json       Structured JSON output
 *   -q, --quiet      Suppress progress (just data to stdout)
 *   -o, --output     Output format (json|table) or file path (CSV/JSON/ZIP)
 *
 * Action commands (agent-grade):
 *   okra upload <source>                            # Upload + wait
 *   okra extract <source> --schema schema.json      # Upload + structured extract
 *   okra extract <docId> --schema schema.json       # Extract from existing doc
 *   okra list                                       # List documents
 *   okra read <id> [--pages 1-5]                    # Full markdown
 *   okra delete <id>                                # Delete document
 *   okra chat "<question>" --doc <id>               # Ask a question
 *   okra collections list                           # List collections
 *   okra collections query <name> "<question>"      # Fan-out -> CSV
 *   okra collections extract <name> --schema s.json # Experimental structured extract -> CSV
 *
 * Review commands:
 *   okra tree / find / page / search / tables / history / toc
 *
 * Exit codes: 0=success, 1=client error, 2=server error
 */

import { Command, CommanderError, InvalidArgumentError, type OutputConfiguration } from 'commander';
import { Effect } from 'effect';
import { authVerificationErrorCause, verifyApiKeyEffect } from './commands/auth';
import {
  checkApiReachabilityEffect,
  checkCliVersionEffect,
  type DoctorCheck,
  type DoctorCheckStatus,
} from './doctor';
export { compareSemver } from './doctor';
import { runCommandEffect } from './command-lifetime';
import {
  waitForJobEffect,
  waitForDocumentMatchEffect,
  type DocumentWaitForMatch,
} from './command-waits';
import {
  ParserProfileRegistryError,
  ParserProfileResolutionError,
  listExecutableParserProfilesEffect,
  resolveExecutableParserProfileByModelPromptEffect,
  resolveExecutableParserProfileEffect,
  type ParserProfile,
} from '@okrapdf/schemas/job';
import { compileComponent } from '@okrapdf/component';
import { loadComponentFile } from '@okrapdf/component/loader';
import { existsSync, realpathSync, readFileSync, writeFileSync } from 'fs';
import { homedir } from 'os';
import { join, resolve } from 'path';
import { pathToFileURL } from 'url';
import packageJson from '../../package.json';
import { OkraClient } from '../client';
import { getContentType, listContentTypes } from '../content-types/registry';
import { OkraRuntimeError } from '../errors';
import {
  DesktopEndpointError,
  DesktopDiscoveryError,
  desktopDiscoveryErrorCause,
  discoverDesktopEndpointEffect,
} from './desktop-endpoint';
import { localToolReport } from '../local/extract';
import {
  WorkbenchClientError,
  WorkbenchOperationError,
  analyzeWorkbenchPiiEffect,
  getWorkbenchRunEffect,
  getWorkbenchRunArtifactsEffect,
  getWorkbenchRunEventsEffect,
  getWorkbenchPiiAnalysisEffect,
  listWorkbenchRunsEffect,
  runWorkbenchParseEffect,
  waitForWorkbenchRunEffect,
  withWorkbenchTransport,
  workbenchErrorCause,
  type WorkbenchProgram,
  type WorkbenchEndpoint,
} from './workbench-client';
import {
  buildDynamicWorkflowDefinition,
  createDynamicWorkflowBuildStepsGuide,
  dynamicWorkflowToCapabilities,
  getDynamicWorkflowExample,
  listDynamicWorkflowExamples,
  validateDynamicWorkflow,
  type DynamicWorkflowDatasetRef,
  type DynamicWorkflowDefinition,
} from '../workflows';
import {
  deployFacet,
  deployLens,
  listFacets,
  listLenses,
  getFacetInfo,
  getLensInfo,
  invokeFacet,
  applyLens,
  getLensState,
  listRuns as listFacetRuns,
  listLensRuns,
  deleteFacet as deleteFacetCmd,
  deleteLens as deleteLensCmd,
  formatFacetList,
  formatLensApply,
  formatRunList,
  LENS_STARTER_SOURCE,
  type FacetCliCtx,
} from './commands/facet';
import {
  find,
  formatFindOutput,
  formatStats,
  search,
  formatSearchOutput,
  authLogin,
  authSetKey,
  authStatus,
  authToken,
  authLogout,
  maskApiKey,
  profileAdd,
  profileUse,
  profileCurrent,
  profileList,
  profileRemove,
  validateSelfHostBundle,
  validateSelfHostBundleEffect,
  formatSelfHostValidationResult,
  createSelfHostDeploymentPlan,
  formatSelfHostDeploymentPlanResult,
  createSelfHostEnvArtifact,
  formatSelfHostEnvArtifactResult,
  createSelfHostComposeArtifact,
  formatSelfHostComposeArtifactResult,
  createSelfHostDockerNetworkHandoffArtifact,
  formatSelfHostDockerNetworkHandoffArtifactResult,
  createSelfHostRailwayConfigArtifact,
  formatSelfHostRailwayConfigArtifactResult,
  createSelfHostRailwayTemplateArtifact,
  formatSelfHostRailwayTemplateArtifactResult,
  createSelfHostRailwayTemplateListingArtifact,
  formatSelfHostRailwayTemplateListingArtifactResult,
  createSelfHostRailwayTemplatePublicationEvidenceArtifact,
  formatSelfHostRailwayTemplatePublicationEvidenceArtifactResult,
  createSelfHostCapabilityImplementationsArtifact,
  formatSelfHostCapabilityImplementationsArtifactResult,
  createSelfHostCapabilityPromotionEvidenceArtifact,
  formatSelfHostCapabilityPromotionEvidenceArtifactResult,
  createSelfHostRailwayPublishEvidenceBundleArtifact,
  formatSelfHostRailwayPublishEvidenceBundleArtifactResult,
  createSelfHostRailwayPublishReadinessArtifact,
  formatSelfHostRailwayPublishReadinessArtifactResult,
  createSelfHostRailwayPublishPackArtifact,
  formatSelfHostRailwayPublishPackArtifactResult,
  materializeSelfHostPublishArtifactsEffect,
  formatSelfHostMaterializeArtifactsResult,
  createSelfHostDraftProof,
  formatSelfHostDraftProof,
  writeSelfHostDraftProof,
  startSelfHostRuntimeServer,
  startCapabilityServiceServer,
  networkBindWarning,
  upload,
  render,
  formatDocumentWorkflowRun,
  parsePolicyJson,
  runDocumentWorkflow,
  formatSelfHostSmokeResult,
  createSelfHostSmokeEvidence,
  runSelfHostSmoke,
  type DocumentWorkflowKind,
  type SelfHostSmokeWorkflow,
  listDocuments,
  formatDocumentList,
  deleteDocument,
  listApiResources,
  getApiResource,
  formatApiResourceCatalog,
  formatApiResourceItem,
  collectionList,
  collectionCreate,
  collectionShow,
  collectionDelete,
  collectionAddDocs,
  collectionRemoveDocs,
  collectionSetVisibility,
  collectionQueryRaw,
  collectionExport,
  formatCollectionList,
  formatCollectionDetail,
  formatCollectionCsv,
  formatCollectionTable,
  formatCollectionExportFlat,
  formatExtractCsv,
  formatExtractTable,
  formatExtractJson,
  scaffoldPlugin,
  formatScaffoldPluginResult,
  listPluginCatalog,
  getPluginCatalogInfo,
  runPluginCatalog,
  formatPluginCatalogList,
  formatPluginCatalogInfo,
  formatPluginCatalogRunResult,
} from './commands';
import { registerContextCommand, runContextAsk } from './commands/context';
import type { LocalViewCommandOptions } from './commands/view';
import {
  getActiveProfile,
  getApiKey,
  getApiKeySource,
  getBaseUrl,
  normalizeBaseUrl as normalizeConfigBaseUrl,
  readGlobalConfig,
} from './config';
import {
  handleError,
  inferCommandFromArgv,
  normalizeGlobalFlags,
  progress,
  setOutputContext,
  writeOutput,
} from './output';
import type { GlobalFlags, MachineFailureEnvelope, NextAction } from './output';

export const CLI_VERSION = packageJson.version;
export const PRIMARY_COMMANDS = [
  'auth',
  'doctor',
  'completions',
  'profile',
  'plugin',
  'resources',
  'content-types',
  'documents',
  'files',
  'jobs',
  'agents',
  'workflows',
  'components',
  'collections',
  'upload',
  'parse',
  'runs',
  'run',
  'pii',
  'audit',
  'redact',
  'extract',
  'render',
  'ask',
  'chat',
  'read',
  'list',
  'open',
  'view',
  'delete',
  'context',
  'self-host',
  'serve',
  'web',
  'capability',
  'source',
  ...listContentTypes().map((contentType) => contentType.cli_noun),
] as const;
export const ADVANCED_COMMANDS = ['status', 'find', 'search', 'facet', 'lens'] as const;
export const PRIMARY_COLLECTION_SUBCOMMANDS = ['list', 'query'] as const;
export const ADVANCED_COLLECTION_SUBCOMMANDS = [
  'create',
  'show',
  'delete',
  'add',
  'remove',
  'publish',
  'unpublish',
  'export',
] as const;
export const ROOT_HELP_FOOTER = [
  '',
  'Command map:',
  '  Ingest/context/extract: upload, documents, files, source, context, parse, extract, invoice, receipt, ask, chat, read, open, view, collections',
  '  Processing state/analysis: runs, run, pii',
  '  Ops: jobs, workflows, components, render, audit, redact, web, serve, capability, self-host, plugin, resources, content-types, agents, profile',
  '  Meta: auth, doctor, completions, help',
  '',
  'Primary workflows:',
  '  okra auth login',
  '  okra upload ./report.pdf                  # add --no-wait to queue; resume with okra jobs wait',
  '  okra documents read doc-abc123 --pages 1-3',
  '  okra chat "Summarize this document" --doc doc-abc123',
  '',
  'Grounded context (agent loop):',
  '  okra context structure doc-abc123',
  '  okra context get "termination clause" --source-id doc-abc123',
  '  okra context ask "What is the guaranteed fee?" --source-id doc-abc123',
  '  okra parse doc-abc123 --model gemini-3-flash --prompt layout-bbox-gemini-multipage@1 # then: okra jobs wait doc-abc123',
  '',
  'More:',
  '  okra extract ./report.pdf --schema ./schema.json',
  '  okra render ./report.py --out report.pdf',
  '  okra audit doc-abc123 --standard wcag',
  '  okra redact doc-abc123 --model local',
  '  okra open doc-abc123 --view review',
  '  okra view ./paper.pdf --web              # stream HTML locally with your provider key',
  '  okra collections query earnings "What changed quarter over quarter?" -o earnings.csv',
  '  okra doctor --json',
  '  okra completions zsh > ~/.zfunc/_okra',
  '  okra workflows steps',
  '  okra workflows build ./workflow.json --json',
  '  okra plugin list                       # browse descriptor and legacy plugin rows',
  '  okra plugin add parser mistral-ocr     # scaffold a descriptor-backed plugin package',
  "  okra plugin run llamaparse-fast -p '{}' # invoke a legacy-backed plugin row",
  '  okra resources list                       # discover every API noun and verb',
  '  okra documents list',
  '',
  'Self-hosting:',
  '  npx @okrapdf/cli web                    # zero-config server + browser workbench',
  '  OKRA_SERVER_PASSWORD=secret npx @okrapdf/cli web --host 0.0.0.0  # share on your network',
  '  okra profile add local --base-url https://my-okra.up.railway.app',
  '  okra self-host --help                     # validate → materialize → proof → railway → smoke',
  '  okra serve ./examples/self-host-runtime --port 8787',
  '',
  'Advanced inspection and local-only commands are intentionally hidden from',
  'default help during the v0.14 clean-house release candidate.',
].join('\n');
export const SELF_HOST_HELP_FOOTER = [
  '',
  'Bundle pipeline (in order):',
  '  okra self-host validate ./examples/self-host-runtime',
  '  okra self-host materialize ./examples/self-host-runtime',
  '  okra self-host plan ./examples/self-host-runtime',
  '  okra self-host env ./examples/self-host-runtime',
  '  okra self-host compose ./examples/self-host-runtime',
  '  okra self-host proof ./examples/self-host-runtime --evidence-out self-host-draft.proof.json',
  '',
  'Railway publishing:',
  '  okra self-host railway ./examples/self-host-runtime',
  '  okra self-host network-plan ./examples/self-host-runtime',
  '  okra self-host template ./examples/self-host-runtime',
  '  okra self-host template-listing ./examples/self-host-runtime',
  '  okra self-host template-evidence ./examples/self-host-runtime',
  '  okra self-host implementations ./examples/self-host-runtime',
  '  okra self-host capability-evidence ./examples/self-host-runtime',
  '  okra self-host evidence-bundle ./examples/self-host-runtime',
  '  okra self-host publish-pack ./examples/self-host-runtime',
  '  okra self-host readiness ./examples/self-host-runtime',
  '',
  'Verify a deployed runtime:',
  '  okra self-host smoke https://my-okra.up.railway.app --workflow both --evidence-out railway-smoke.evidence.json',
  '  okra serve ./examples/self-host-runtime --port 8787',
  '  okra capability serve parser.mineru --port 8080',
].join('\n');
export const COLLECTION_HELP_FOOTER = [
  '',
  'Stable v0.14 collection workflow:',
  '  okra collections query <name> "<question>"',
  '',
  'Experimental structured fan-out remains available via:',
  '  okra collections query <name> "<question>" --schema ./schema.json',
  '  okra collections extract <name> --schema ./schema.json',
  '',
  'Advanced collection management commands remain available but are',
  'intentionally hidden from default help during the clean-house release',
  'candidate.',
].join('\n');

export const program = new Command();
program.showHelpAfterError();
program.showSuggestionAfterError();

program
  .name('okra')
  .description('okraPDF CLI — upload PDFs, chat with documents, and extract structured data')
  .version(CLI_VERSION)
  .option('-j, --json', 'Output JSON (structured, machine-readable)')
  .option('-q, --quiet', 'Suppress progress and human-readable frills')
  .option('-o, --output <format-or-file>', 'Output format (json|table) or write output to file');
program.addHelpText('after', ROOT_HELP_FOOTER);

/** Read global flags from program.opts(). */
function globals(): GlobalFlags {
  const command = inferCommandFromArgv(process.argv.slice(2));
  const g = normalizeGlobalFlags(program.opts(), process.stdout.isTTY === true, command);
  setOutputContext({
    json: g.json === true,
    command,
  });
  return g;
}

export function getMissingApiKeyMessage(): string {
  return [
    'No API key found.',
    '',
    'Set one up with:',
    '  okra auth login',
    '  export OKRA_API_KEY="okra_xxx"',
    '',
    'Get your API key at:',
    '  https://app.okrapdf.com/settings',
    '',
    'CLI docs:',
    '  https://docs.okrapdf.com/api-reference/cli',
  ].join('\n');
}

/**
 * Terminal "no API key" failure — the most common agent error. In JSON mode emit
 * the {ok:false} failure envelope on STDOUT (so `okra … --json | jq` parses it,
 * with recovery next_actions); in human mode keep the rich multi-line setup
 * message on stderr. Exits non-zero. Replaces three inlined copies that wrote
 * `{ error, code }` to stderr and broke the JSON contract.
 */
function createMissingApiKeyError(): OkraRuntimeError {
  const message = 'No API key found. Set OKRA_API_KEY=okra_… or run `okra auth login`.';
  return new OkraRuntimeError('UNAUTHORIZED', message, 401, {
    error: 'no_api_key',
    message,
    hint: 'Run `okra auth login` or set OKRA_API_KEY.',
    retryable: false,
    next_actions: [
      { cmd: 'okra auth login', why: 'Authenticate interactively.' },
      {
        cmd: 'export OKRA_API_KEY="okra_…"',
        why: 'Use your paid-tier API key (https://app.okrapdf.com/settings).',
      },
    ],
  });
}

function formatDocumentReadyMessage(docId: string, pages?: number): string {
  return [
    `Ready: ${docId}${typeof pages === 'number' ? ` (${pages} pages)` : ''}`,
    '',
    'Next:',
    `  okra chat "Summarize this document" --doc ${docId}`,
    `  okra read ${docId}`,
    `  okra extract ${docId} --schema ./schema.json`,
  ].join('\n');
}

function formatQueuedDocumentMessage(docId: string, jobId?: string): string {
  return [
    `Queued: ${docId}`,
    '',
    'Next:',
    jobId ? `  okra jobs wait ${jobId}` : `  okra jobs wait ${docId}`,
    `  okra status ${docId}`,
    '',
    'Once processing finishes:',
    `  okra chat "Summarize this document" --doc ${docId}`,
    `  okra read ${docId}`,
  ].join('\n');
}

function isDirectExecution(): boolean {
  const entry = process.argv[1];
  if (!entry) return false;
  try {
    return import.meta.url === pathToFileURL(realpathSync(resolve(entry))).href;
  } catch {
    return import.meta.url === pathToFileURL(resolve(entry)).href;
  }
}

// Create client with proper config priority:
// 1. Environment variable (OKRA_API_KEY)
// 2. Project config (.okrarc, .okra.json)
// 3. Global config (~/.okra/config.json)
function getClient(): OkraClient {
  const apiKey = getApiKey();
  const baseUrl = getBaseUrl();

  if (!apiKey) {
    throw createMissingApiKeyError();
  }

  return new OkraClient({ apiKey, baseUrl });
}

function parsePort(raw: string | number | undefined): number {
  const value = typeof raw === 'number' ? raw : Number.parseInt(raw ?? '8787', 10);
  if (!Number.isInteger(value) || value < 0 || value > 65535) {
    throw new Error('--port must be an integer between 0 and 65535');
  }
  return value;
}

function parsePositiveInt(
  raw: string | number | undefined,
  flagName: string,
  fallback: number,
): number {
  const value = typeof raw === 'number' ? raw : Number.parseInt(raw ?? String(fallback), 10);
  if (!Number.isInteger(value) || value < 1) {
    throw new Error(`${flagName} must be a positive integer`);
  }
  return value;
}

function parseLocalViewPort(raw: string): number {
  const value = Number(raw);
  if (!Number.isInteger(value) || value < 0 || value > 65535) {
    throw new InvalidArgumentError('must be an integer between 0 and 65535');
  }
  return value;
}

function parseLocalViewPositiveInt(raw: string, flagName: string): number {
  const value = Number(raw);
  if (!Number.isInteger(value) || value < 1) {
    throw new InvalidArgumentError(`${flagName} must be a positive integer`);
  }
  return value;
}

function parseSmokeWorkflow(raw: string | undefined): SelfHostSmokeWorkflow {
  const value = raw ?? 'audit';
  if (value === 'audit' || value === 'redact' || value === 'both') return value;
  throw new Error('--workflow must be one of: audit, redact, both');
}

interface ServeStartupDiagnostics {
  persistence: { backend: 'postgres' | 'filesystem'; data_dir: string };
  local_tools: {
    ok: boolean;
    pdftotext: boolean;
    pdfinfo: boolean;
    pdftoppm: boolean;
    tesseract: boolean;
  } | null;
  lines: string[];
}

/**
 * What an operator needs to see once at startup: where document state goes,
 * and whether the local extraction toolchain that `parse` depends on is
 * actually installed. Without this, a host missing poppler serves a runtime
 * that "works" until the first parse returns "extraction unavailable".
 */
function serveStartupDiagnostics(
  env: Record<string, string | undefined>,
  builtin: boolean,
  root: string,
): ServeStartupDiagnostics {
  const dataDir =
    env.OKRA_DATA_DIR?.trim() ||
    (builtin ? join(homedir(), '.okra', 'serve') : join(root, '.runtime-data'));
  const backend = env.DATABASE_URL?.trim() ? 'postgres' : 'filesystem';
  const lines = [
    `Persistence: ${backend === 'postgres' ? 'postgres (DATABASE_URL)' : `filesystem (${dataDir})`}`,
  ];
  let local_tools: ServeStartupDiagnostics['local_tools'] = null;
  try {
    const report = localToolReport(dataDir);
    local_tools = {
      ok: report.ok,
      pdftotext: report.tools.pdftotext.available,
      pdfinfo: report.tools.pdfinfo.available,
      pdftoppm: report.tools.pdftoppm.available,
      tesseract: report.tools.tesseract.available,
    };
  } catch {
    return { persistence: { backend, data_dir: dataDir }, local_tools, lines };
  }
  const tools = local_tools;
  lines.push(
    `Local extraction: ${(['pdftotext', 'pdfinfo', 'pdftoppm', 'tesseract'] as const)
      .map((tool) => `${tool} ${tools[tool] ? 'ok' : 'missing'}`)
      .join(' · ')}`,
  );
  if (!tools.ok) {
    lines.push(
      'WARNING: pdftotext/pdfinfo not found - parse will report "extraction unavailable". Install poppler-utils (macOS: brew install poppler; Debian/Ubuntu: apt-get install poppler-utils), or run the Docker image, which bundles them.',
    );
  } else if (!tools.pdftoppm || !tools.tesseract) {
    lines.push(
      "Note: pdftoppm/tesseract not found - pages without a text layer will not be OCR'd (macOS: brew install tesseract; Debian/Ubuntu: apt-get install tesseract-ocr).",
    );
  }
  return { persistence: { backend, data_dir: dataDir }, local_tools, lines };
}

async function runSelfHostServeCommand(
  bundleDir?: string,
  options: { host?: string; port?: string; publicDir?: string; open?: boolean } = {},
): Promise<void> {
  const g = globals();
  try {
    const env = { ...process.env };
    const configuredBundle = bundleDir ?? env.OKRA_SELF_HOST_BUNDLE;
    const builtin = !configuredBundle;
    const resolvedBundle = configuredBundle ?? process.cwd();
    let validation: ReturnType<typeof validateSelfHostBundle> | null = null;
    if (!builtin) {
      validation = validateSelfHostBundle(resolvedBundle);
      if (!validation.ok) {
        writeOutput(formatSelfHostValidationResult(validation, g.json), g.output);
        process.exitCode = 1;
        return;
      }
    }

    const host = options.host ?? env.HOST ?? '127.0.0.1';
    const started = await startSelfHostRuntimeServer({
      bundleDir: resolvedBundle,
      builtin,
      host,
      port: parsePort(options.port ?? env.PORT),
      env,
      ...(options.publicDir ? { publicDir: options.publicDir } : {}),
    });

    const publicUrl = env.OKRA_PUBLIC_URL?.replace(/\/+$/, '');
    const diagnostics = serveStartupDiagnostics(env, builtin, validation?.root ?? resolvedBundle);
    const startup = {
      object: 'self_host_server',
      ok: true,
      url: started.url,
      web: started.url,
      protocol: 'okra.client.v1',
      mode: builtin ? 'builtin' : 'bundle',
      root: validation?.root ?? resolvedBundle,
      health: `${started.url}/health`,
      client_health: `${started.url}/global/health`,
      status: `${started.url}/v1/self-host/status`,
      persistence: diagnostics.persistence,
      local_tools: diagnostics.local_tools,
      ...(publicUrl ? { share_url: publicUrl } : {}),
    };

    const bindWarning = networkBindWarning(host, env);
    if (g.json) {
      writeOutput(JSON.stringify(startup), g.output);
    } else {
      process.stderr.write(
        [
          `okra self-host runtime listening on ${started.url}`,
          `Mode: ${builtin ? 'zero-config local workbench' : `bundle ${validation?.root}`}`,
          `Web: ${startup.web}`,
          `Protocol: ${startup.protocol}`,
          ...diagnostics.lines,
          ...(publicUrl ? [`Share: ${publicUrl}`] : []),
          ...(bindWarning ? [bindWarning] : []),
          '',
        ].join('\n'),
      );
    }

    if (options.open) {
      try {
        const { openLocalBrowser } = await import('./commands/view');
        await openLocalBrowser(started.url);
      } catch (error) {
        process.stderr.write(
          `Could not open the browser automatically: ${error instanceof Error ? error.message : String(error)}\n`,
        );
      }
    }

    const shutdown = async () => {
      await started.close();
    };
    process.once('SIGINT', () => {
      void shutdown();
    });
    process.once('SIGTERM', () => {
      void shutdown();
    });
    await new Promise<void>((resolveClose) => {
      started.server.once('close', resolveClose);
    });
  } catch (error) {
    handleError(error, g.json);
  }
}

async function runCapabilityServeCommand(
  capabilityId: string,
  options: { host?: string; port?: string } = {},
): Promise<void> {
  const g = globals();
  try {
    const started = await startCapabilityServiceServer({
      capabilityId,
      host: options.host ?? process.env.HOST ?? '0.0.0.0',
      port: parsePort(
        options.port ?? process.env.OKRA_CAPABILITY_HTTP_PORT ?? process.env.PORT ?? '8080',
      ),
    });

    const startup = {
      object: 'capability_service_server',
      ok: true,
      protocol: 'okra-capability-http/v1',
      capability_id: capabilityId,
      url: started.url,
      health: `${started.url}/health`,
      endpoint: `${started.url}/v1/capability-runs`,
    };

    if (g.json) {
      writeOutput(JSON.stringify(startup), g.output);
    } else {
      process.stderr.write(
        [
          `okra capability service listening on ${started.url}`,
          `Capability: ${capabilityId}`,
          `Protocol: ${startup.protocol}`,
          `Endpoint: ${startup.endpoint}`,
          '',
        ].join('\n'),
      );
    }

    const shutdown = async () => {
      await started.close();
    };
    process.once('SIGINT', () => {
      void shutdown();
    });
    process.once('SIGTERM', () => {
      void shutdown();
    });
    await new Promise<void>((resolveClose) => {
      started.server.once('close', resolveClose);
    });
  } catch (error) {
    handleError(error, g.json);
  }
}

// ============================================================================
// upload command
// ============================================================================
async function readStdinUploadBytes(): Promise<Uint8Array> {
  const chunks: Uint8Array[] = [];
  for await (const chunk of process.stdin) {
    chunks.push(typeof chunk === 'string' ? Buffer.from(chunk) : new Uint8Array(chunk));
  }
  return Buffer.concat(chunks);
}

async function runUploadCommand(
  source: string,
  options: { wait?: boolean; vendorOptions?: string; waitTimeout?: string },
): Promise<void> {
  const g = globals();
  try {
    const client = getClient();
    let vendorOptions: Record<string, unknown> | undefined;
    if (options.vendorOptions) {
      try {
        vendorOptions = JSON.parse(options.vendorOptions);
      } catch {
        throw new OkraRuntimeError('INVALID_REQUEST', '--vendor-options must be valid JSON.', 400, {
          error: 'invalid_request',
          message: '--vendor-options must be valid JSON.',
          next_actions: [
            {
              cmd: 'okra upload <file> --vendor-options \'{"key":"value"}\'',
              why: 'Pass a valid JSON object.',
            },
          ],
        });
      }
    }
    const waitTimeoutMs = parsePositiveSecondsOption(options.waitTimeout, '--wait-timeout');
    const uploadSource = source === '-' ? await readStdinUploadBytes() : source;

    const result = await upload(client, uploadSource, {
      ...g,
      noWait: options.wait === false,
      vendorOptions,
      waitTimeoutMs,
      ...(source === '-' ? { fileName: 'stdin.pdf' } : {}),
    });

    if (g.json) {
      writeOutput(JSON.stringify(result), g.output);
    } else {
      writeOutput(
        options.wait === false
          ? formatQueuedDocumentMessage(result.id, result.job_id ?? undefined)
          : formatDocumentReadyMessage(result.id, result.pages),
        g.output,
      );
    }
  } catch (error) {
    handleError(error, g.json);
  }
}

function writeJsonOrPretty(value: unknown, asJson: boolean | undefined, output?: string): void {
  writeOutput(JSON.stringify(value, null, asJson ? 0 : 2), output);
}

function shellQuote(value: string): string {
  return /^[A-Za-z0-9._:/=@-]+$/.test(value) ? value : JSON.stringify(value);
}

function createFailureEnvelope(
  command: string,
  error: string,
  message: string,
  nextActions: Array<{ cmd: string; why: string }>,
  extra: Record<string, unknown> = {},
): MachineFailureEnvelope {
  const hint = typeof extra.hint === 'string' ? extra.hint : nextActions[0]?.why;
  const retryable = typeof extra.retryable === 'boolean' ? extra.retryable : false;
  const exitCode = typeof extra.exit_code === 'number' ? extra.exit_code : 1;
  return {
    ok: false,
    command,
    error,
    message,
    // Every failure envelope carries a numeric `code` like the handleError path
    // (the skill documents it). CLI-constructed failures (confirmation/human-
    // review gates, not_supported_on_cloud) are client-side request rejections,
    // so 400 by default; a call site can override via `extra.code`.
    code: 400,
    ...(hint ? { hint } : {}),
    retryable,
    exit_code: exitCode,
    cost: { usd: null },
    citations: [],
    next_actions: nextActions,
    ...extra,
  };
}

function checkNodeRuntime(): DoctorCheck {
  const major = Number.parseInt(process.versions.node.split('.')[0] ?? '0', 10);
  const ok = Number.isInteger(major) && major >= 20;
  return {
    id: 'node',
    status: ok ? 'ok' : 'fail',
    title: 'Node.js',
    detail: ok
      ? `Node ${process.version} satisfies the okra CLI runtime requirement.`
      : `Node ${process.version} is too old; okra CLI requires Node 20 or newer.`,
    data: {
      version: process.version,
      major,
      required: '>=20',
    },
    next_actions: ok
      ? []
      : [{ cmd: 'nvm use 22 || brew install node', why: 'Run okra with Node 20 or newer.' }],
  };
}

function checkSelfHostEnv(): DoctorCheck {
  const bundle = process.env.OKRA_SELF_HOST_BUNDLE;
  const dataDir = process.env.OKRA_DATA_DIR;
  const endpoints = process.env.OKRA_CAPABILITY_ENDPOINTS;
  const configured = Boolean(
    bundle || dataDir || endpoints || process.env.OKRA_CAPABILITY_HTTP_PORT,
  );
  const endpointJsonValid = (() => {
    if (!endpoints) return null;
    try {
      JSON.parse(endpoints);
      return true;
    } catch {
      return false;
    }
  })();
  const bundleExists = bundle ? existsSync(bundle) : null;
  const dataDirExists = dataDir ? existsSync(dataDir) : null;

  if (bundleExists === false) {
    return {
      id: 'self_host_env',
      status: 'fail',
      title: 'Self-host environment',
      detail: `OKRA_SELF_HOST_BUNDLE points at a missing path: ${bundle}`,
      data: {
        configured,
        bundle,
        bundle_exists: false,
        data_dir: dataDir ?? null,
        data_dir_exists: dataDirExists,
        capability_endpoints_valid: endpointJsonValid,
      },
      next_actions: [
        {
          cmd: 'okra self-host validate ./examples/self-host-runtime',
          why: 'Validate an existing self-host bundle path.',
        },
      ],
    };
  }

  if (endpointJsonValid === false) {
    return {
      id: 'self_host_env',
      status: 'fail',
      title: 'Self-host environment',
      detail: 'OKRA_CAPABILITY_ENDPOINTS is set but is not valid JSON.',
      data: {
        configured,
        bundle: bundle ?? null,
        bundle_exists: bundleExists,
        data_dir: dataDir ?? null,
        data_dir_exists: dataDirExists,
        capability_endpoints_valid: false,
      },
      next_actions: [
        {
          cmd: 'okra self-host env ./examples/self-host-runtime',
          why: 'Regenerate self-host environment variables.',
        },
      ],
    };
  }

  const warns: string[] = [];
  if (dataDir && dataDirExists === false)
    warns.push(`OKRA_DATA_DIR does not exist yet: ${dataDir}`);
  if (!configured) warns.push('No self-host environment overrides are set.');

  return {
    id: 'self_host_env',
    status: warns.length > 0 && configured ? 'warn' : 'ok',
    title: 'Self-host environment',
    detail:
      warns.length > 0
        ? warns.join(' ')
        : configured
          ? 'Self-host environment variables are syntactically valid.'
          : 'No self-host environment overrides are set.',
    data: {
      configured,
      bundle: bundle ?? null,
      bundle_exists: bundleExists,
      data_dir: dataDir ?? null,
      data_dir_exists: dataDirExists,
      capability_endpoints_configured: Boolean(endpoints),
      capability_endpoints_valid: endpointJsonValid,
    },
    next_actions:
      warns.length > 0 && configured
        ? [
            {
              cmd: 'okra self-host env ./examples/self-host-runtime',
              why: 'Regenerate or inspect expected self-host environment variables.',
            },
          ]
        : [],
  };
}

interface DoctorReport {
  object: 'doctor_report';
  ok: boolean;
  command: 'doctor';
  base_url: string;
  checks: DoctorCheck[];
  next_actions: NextAction[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function normalizeDoctorBaseUrl(rawBaseUrl: string): { baseUrl: string; error?: string } {
  try {
    return { baseUrl: normalizeConfigBaseUrl(rawBaseUrl) };
  } catch (error) {
    return {
      baseUrl: rawBaseUrl.trim(),
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

async function runDoctorCommand(options: { baseUrl?: string } = {}): Promise<void> {
  const g = globals();
  const report = await runCommandEffect(
    Effect.gen(function* () {
      const rawBaseUrl = options.baseUrl || getBaseUrl() || 'https://api.okrapdf.com';
      const { baseUrl, error: baseUrlError } = normalizeDoctorBaseUrl(rawBaseUrl);
      const apiKey = getApiKey();
      const apiKeySource = getApiKeySource();
      const globalConfig = readGlobalConfig();
      const activeProfile = getActiveProfile(globalConfig);
      const checks: DoctorCheck[] = [];

      checks.push(checkNodeRuntime());
      checks.push({
        id: 'config',
        status: baseUrlError ? 'fail' : apiKey ? 'ok' : 'warn',
        title: 'Configuration',
        detail: baseUrlError
          ? `Base URL is invalid: ${baseUrlError}`
          : apiKey
            ? `Using API key from ${apiKeySource}.`
            : 'No API key is configured.',
        data: {
          api_key_source: apiKeySource,
          api_key_masked: apiKey ? maskApiKey(apiKey) : null,
          base_url: baseUrl,
          base_url_source: options.baseUrl ? 'flag (--base-url)' : 'configuration',
          active_profile: activeProfile?.name ?? null,
        },
        next_actions: apiKey
          ? []
          : [
              { cmd: 'okra auth login', why: 'Save an API key for authenticated requests.' },
              {
                cmd: 'export OKRA_API_KEY="okra_xxx"',
                why: 'Use an API key for this shell session.',
              },
            ],
      });

      if (!apiKey) {
        checks.push({
          id: 'auth',
          status: 'fail',
          title: 'Authentication',
          detail: 'No API key is configured, so live authentication cannot be verified.',
          data: { authenticated: false, source: apiKeySource },
          next_actions: [
            {
              cmd: 'okra auth login',
              why: 'Save an API key before running authenticated CLI workflows.',
            },
            {
              cmd: 'export OKRA_API_KEY="okra_xxx"',
              why: 'Provide an API key without writing config.',
            },
          ],
        });
      } else if (baseUrlError) {
        checks.push({
          id: 'auth',
          status: 'fail',
          title: 'Authentication',
          detail: `Cannot verify API key because the base URL is invalid: ${baseUrlError}`,
          data: { authenticated: false, source: apiKeySource },
          next_actions: [
            {
              cmd: 'okra doctor --base-url https://api.okrapdf.com',
              why: 'Run auth verification against a valid API endpoint.',
            },
          ],
        });
      } else {
        checks.push(
          yield* verifyApiKeyEffect(apiKey, { baseUrl }).pipe(
            Effect.map((verified): DoctorCheck => {
              return {
                id: 'auth',
                status: 'ok',
                title: 'Authentication',
                detail: `Authenticated as ${verified.user_id}.`,
                data: {
                  authenticated: true,
                  user_id: verified.user_id,
                  key_id: verified.key_id,
                  key_name: verified.key_name ?? null,
                  key_type: verified.key_type ?? null,
                  scope: verified.scope ?? null,
                  scoped_orgs: verified.scoped_orgs ?? [],
                  scoped_projects: verified.scoped_projects ?? [],
                },
                next_actions: [],
              };
            }),
            Effect.catch((failure) =>
              Effect.sync((): DoctorCheck => {
                const error = authVerificationErrorCause(failure);
                const status = error instanceof OkraRuntimeError ? error.status : null;
                return {
                  id: 'auth',
                  status: 'fail',
                  title: 'Authentication',
                  detail: error instanceof Error ? error.message : String(error),
                  data: {
                    authenticated: false,
                    status,
                    source: apiKeySource,
                  },
                  next_actions: [
                    { cmd: 'okra auth login', why: 'Replace the configured API key.' },
                    {
                      cmd: `okra doctor --base-url ${shellQuote(baseUrl)}`,
                      why: 'Re-run diagnostics after updating credentials or endpoint settings.',
                    },
                  ],
                };
              }),
            ),
          ),
        );
      }

      checks.push(yield* checkApiReachabilityEffect(baseUrl, baseUrlError));
      checks.push(yield* checkCliVersionEffect(packageJson.version));
      checks.push(checkSelfHostEnv());

      const nextActions = checks
        .filter((check) => check.status === 'fail')
        .flatMap((check) => check.next_actions);
      const report: DoctorReport = {
        object: 'doctor_report',
        ok: checks.every((check) => check.status !== 'fail'),
        command: 'doctor',
        base_url: baseUrl,
        checks,
        next_actions: nextActions,
      };

      return report;
    }),
  );
  if (!report) return;

  if (!report.ok) {
    process.exitCode = 1;
  }

  if (g.json) {
    writeOutput(JSON.stringify(report), g.output);
  } else {
    writeOutput(formatDoctorReport(report), g.output);
  }
}

function formatDoctorReport(report: DoctorReport): string {
  const statusLabel: Record<DoctorCheckStatus, string> = {
    ok: 'OK',
    warn: 'WARN',
    fail: 'FAIL',
    unknown: 'UNKNOWN',
  };
  const lines = [
    'okra doctor',
    `Base URL: ${report.base_url}`,
    '',
    ...report.checks.map((check) => `${statusLabel[check.status]} ${check.title}: ${check.detail}`),
  ];

  if (report.next_actions.length > 0) {
    lines.push('', 'Next:');
    for (const action of report.next_actions) {
      lines.push(`  ${action.cmd}  # ${action.why}`);
    }
  }

  return lines.join('\n');
}

async function promptForYes(question: string): Promise<boolean> {
  const readline = await import('readline');
  const rl = readline.createInterface({
    input: process.stdin,
    output: process.stderr,
  });

  return new Promise((resolvePrompt) => {
    rl.question(question, (answer) => {
      rl.close();
      resolvePrompt(/^(y|yes)$/i.test(answer.trim()));
    });
  });
}

async function requirePublicPublishRightsConfirmation(
  command: string,
  target: string,
  flags: GlobalFlags,
  confirmed: boolean | undefined,
): Promise<boolean> {
  if (confirmed) return true;

  const message = 'Public publish asserts that you own or are licensed to publish this content.';
  const nextCommand = `okra ${command} ${shellQuote(target)} --confirm-rights`;
  const gate = {
    id: 'public_publish_rights',
    kind: 'public_publish',
    confirm_flag: '--confirm-rights',
  };

  if (flags.json || !process.stdin.isTTY || !process.stdout.isTTY) {
    writeOutput(
      JSON.stringify(
        createFailureEnvelope(
          command,
          'confirmation_required',
          message,
          [
            {
              cmd: nextCommand,
              why: 'Confirm the public publish rights gate explicitly.',
            },
          ],
          { gate },
        ),
      ),
      flags.output,
    );
    process.exitCode = 1;
    return false;
  }

  const ok = await promptForYes(`${message}\nProceed? [y/N] `);
  if (!ok) {
    writeOutput('Cancelled: public publish rights were not confirmed.', flags.output);
    process.exitCode = 1;
  }
  return ok;
}

function writeHumanReviewRequired(command: string, documentId: string, flags: GlobalFlags): void {
  const message =
    'Machine audit results remain Audited; Attested requires the live human review gate.';
  const envelope = createFailureEnvelope(
    command,
    'human_review_required',
    message,
    [
      {
        cmd: `okra open ${shellQuote(documentId)} --view audit`,
        why: 'Resolve the Audited -> Attested gate in the live report.',
      },
    ],
    {
      state: 'audited',
      gate: {
        id: 'audit_attestation',
        kind: 'human_attestation',
        required_view: 'audit',
      },
    },
  );

  if (flags.json || !process.stdout.isTTY) {
    writeOutput(JSON.stringify(envelope), flags.output);
  } else {
    writeOutput(
      [`Refused: ${message}`, '', 'Next:', `  okra open ${documentId} --view audit`].join('\n'),
      flags.output,
    );
  }
  process.exitCode = 1;
}

function parseLimitOption(raw: string | undefined): number | undefined {
  if (raw == null) return undefined;
  const limit = Number.parseInt(raw, 10);
  if (!Number.isFinite(limit) || limit < 1) {
    throw new Error('--limit must be a positive integer');
  }
  return limit;
}

function parsePositiveSecondsOption(raw: string | undefined, flagName: string): number | undefined {
  if (raw == null) return undefined;
  const seconds = Number(raw);
  if (!Number.isFinite(seconds) || seconds <= 0) {
    throw new Error(`${flagName} must be a positive number of seconds`);
  }
  return Math.ceil(seconds * 1000);
}

function parseFileTransport(raw: string | undefined): 'auto' | 'multipart' | 'direct' | undefined {
  if (!raw) return undefined;
  if (raw === 'auto' || raw === 'multipart' || raw === 'direct') return raw;
  throw new Error('--transport must be one of: auto, multipart, direct');
}

function formatBytes(value: unknown): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '-';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function extractListItems(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== 'object') return [];
  const record = value as Record<string, unknown>;
  for (const key of ['data', 'documents', 'collections', 'agents', 'items', 'runs', 'workflows']) {
    if (Array.isArray(record[key])) return record[key] as unknown[];
  }
  return [];
}

function formatGenericList(value: unknown, json?: boolean): string {
  if (json) return JSON.stringify(value);
  const rows = extractListItems(value);
  if (rows.length === 0) return 'No items found.';
  return rows
    .map((item) => {
      if (!item || typeof item !== 'object') return String(item);
      const record = item as Record<string, unknown>;
      return [
        record.id ?? record.name ?? record.slug ?? '(unknown)',
        record.name ?? record.type ?? record.status ?? '',
        record.status ?? record.phase ?? '',
        record.created_at ?? record.created ?? '',
      ]
        .filter((part) => part !== '')
        .join('\t');
    })
    .join('\n');
}

function formatDocumentStatus(
  docId: string,
  status: Record<string, unknown>,
  json?: boolean,
): string {
  if (json) return JSON.stringify(status);
  const lines = [`Document: ${docId}`, `Phase: ${status.phase ?? '-'}`];
  const pagesCompleted = status.pagesCompleted ?? status.pages_completed;
  const pagesTotal = status.pagesTotal ?? status.pages_total ?? status.totalPages;
  if (pagesCompleted != null || pagesTotal != null) {
    lines.push(`Pages: ${pagesCompleted ?? '?'} / ${pagesTotal ?? '?'}`);
  }
  return lines.join('\n');
}

function documentUrls(client: OkraClient, docId: string): Record<string, unknown> {
  const encoded = encodeURIComponent(docId);
  const base = `${client.url}/v1/documents/${encoded}`;
  return {
    object: 'document_urls',
    id: docId,
    urls: {
      self: base,
      status: base,
      markdown: `${base}/full.md`,
      pages: `${base}/pages`,
      page_markdown_template: `${base}/pg_{N}.md`,
      page_image_template: `${base}/d_shimmer/pg_{N}.png`,
      original_pdf: `${base}/original.pdf`,
      download: client.downloadUrl(docId),
      chat_completions: `${base}/chat/completions`,
      legacy_model_endpoint: client.modelEndpoint(docId),
    },
  };
}

function documentShellUrl(docId: string, options: { view?: string } = {}): string {
  const baseUrl = (getBaseUrl() || 'https://api.okrapdf.com').replace(/\/+$/, '');
  const view = options.view?.trim() || 'document';
  const params = new URLSearchParams({ doc: docId, view });
  return `${baseUrl}/?${params.toString()}`;
}

function formatFileList(value: { data?: Array<Record<string, unknown>> }, json?: boolean): string {
  if (json) return JSON.stringify(value);
  const files = value.data ?? [];
  if (files.length === 0) return 'No files found.';
  const lines = ['ID\tName\tSize\tCreated'];
  for (const file of files) {
    lines.push(
      [
        file.id ?? file.file_id ?? '-',
        file.name ?? '-',
        formatBytes(file.bytes ?? file.size),
        file.created_at ?? '-',
      ].join('\t'),
    );
  }
  return lines.join('\n');
}

function formatJobList(value: { data?: Array<Record<string, unknown>> }, json?: boolean): string {
  if (json) return JSON.stringify(value);
  const jobs = value.data ?? [];
  if (jobs.length === 0) return 'No jobs found.';
  const lines = ['ID\tType\tStatus\tCreated'];
  for (const job of jobs) {
    lines.push([job.id ?? '-', job.type ?? '-', job.status ?? '-', job.created ?? '-'].join('\t'));
  }
  return lines.join('\n');
}

async function runResourceListCommand(): Promise<void> {
  const g = globals();
  try {
    const client = getClient();
    const catalog = await listApiResources(client);
    writeOutput(formatApiResourceCatalog(catalog, g.json), g.output);
  } catch (error) {
    handleError(error, g.json);
  }
}

async function runResourceShowCommand(name: string): Promise<void> {
  const g = globals();
  try {
    const client = getClient();
    const resource = await getApiResource(client, name);
    writeOutput(formatApiResourceItem(resource, g.json), g.output);
  } catch (error) {
    handleError(error, g.json);
  }
}

// content-types — discover the typed extraction contracts (#330/#331). Local
// registry, no network; the manifest grammar lives in src/content-types/.
function runContentTypesListCommand(): void {
  const g = globals();
  try {
    writeOutput(JSON.stringify({ content_types: listContentTypes() }), g.output);
  } catch (error) {
    handleError(error, g.json);
  }
}

function runContentTypesShowCommand(id: string): void {
  const g = globals();
  try {
    const manifest = getContentType(id);
    if (!manifest) {
      throw new OkraRuntimeError('NOT_FOUND', `No content type "${id}"`, 404, {
        error: 'not_found',
        message: `No content type "${id}". Run \`okra content-types list\` to see available content types.`,
        next_actions: [
          { cmd: 'okra content-types list', why: 'List the registered content types.' },
        ],
      });
    }
    writeOutput(JSON.stringify(manifest), g.output);
  } catch (error) {
    handleError(error, g.json);
  }
}

async function runDocumentListCommand(): Promise<void> {
  const g = globals();
  try {
    const client = getClient();
    const { documents } = await listDocuments(client, g);
    writeOutput(formatDocumentList(documents, g.json), g.output);
  } catch (error) {
    handleError(error, g.json);
  }
}

async function runDocumentDeleteCommand(docId: string): Promise<void> {
  const g = globals();
  try {
    const client = getClient();
    const result = await deleteDocument(client, docId, g);
    if (g.json) {
      writeOutput(JSON.stringify(result), g.output);
    } else {
      writeOutput(`Deleted ${docId}`, g.output);
    }
  } catch (error) {
    handleError(error, g.json);
  }
}

async function runDocumentStatusCommand(docId: string): Promise<void> {
  const g = globals();
  try {
    const client = getClient();
    const status = (await client.status(docId)) as unknown as Record<string, unknown>;
    writeOutput(formatDocumentStatus(docId, status, g.json), g.output);
  } catch (error) {
    handleError(error, g.json);
  }
}

async function runDocumentReadCommand(
  docId: string,
  options: { pages?: string } = {},
): Promise<void> {
  const g = globals();
  try {
    const client = getClient();
    const result = await client.read(docId, { pages: options.pages });
    if (g.json) {
      writeOutput(JSON.stringify(result), g.output);
    } else {
      writeOutput(result.markdown, g.output);
    }
  } catch (error) {
    handleError(error, g.json);
  }
}

async function runDocumentUrlsCommand(docId: string): Promise<void> {
  const g = globals();
  try {
    const client = getClient();
    const urls = documentUrls(client, docId);
    if (g.json) {
      writeOutput(JSON.stringify(urls), g.output);
    } else {
      const urlMap = (urls.urls ?? {}) as Record<string, unknown>;
      writeOutput(
        Object.entries(urlMap)
          .map(([key, value]) => `${key}\t${String(value)}`)
          .join('\n'),
        g.output,
      );
    }
  } catch (error) {
    handleError(error, g.json);
  }
}

async function runFileListCommand(
  options: { limit?: string; cursor?: string } = {},
): Promise<void> {
  const g = globals();
  try {
    const client = getClient();
    const files = await client.listFiles({
      limit: parseLimitOption(options.limit),
      cursor: options.cursor,
    });
    writeOutput(
      formatFileList(files as unknown as { data?: Array<Record<string, unknown>> }, g.json),
      g.output,
    );
  } catch (error) {
    handleError(error, g.json);
  }
}

async function runFileGetCommand(fileId: string): Promise<void> {
  const g = globals();
  try {
    const client = getClient();
    const file = (await client.getFile(fileId)) as unknown as Record<string, unknown>;
    writeJsonOrPretty(file, g.json, g.output);
  } catch (error) {
    handleError(error, g.json);
  }
}

async function runFileUploadCommand(
  source: string,
  options: { fileName?: string; transport?: string } = {},
): Promise<void> {
  const g = globals();
  try {
    const client = getClient();
    const file = (await client.files.upload(source, {
      fileName: options.fileName,
      transport: parseFileTransport(options.transport),
    })) as unknown as Record<string, unknown>;
    if (g.json) {
      writeOutput(JSON.stringify(file), g.output);
      return;
    }
    writeOutput(
      [
        `File: ${file.id ?? file.file_id ?? '-'}`,
        `Name: ${file.name ?? '-'}`,
        `Size: ${formatBytes(file.bytes ?? file.size)}`,
        `Bytes: ${client.fileDownloadUrl(String(file.id ?? file.file_id ?? ''))}`,
      ].join('\n'),
      g.output,
    );
  } catch (error) {
    handleError(error, g.json);
  }
}

async function runFileDeleteCommand(fileId: string): Promise<void> {
  const g = globals();
  try {
    const client = getClient();
    const result = await client.deleteFile(fileId);
    if (g.json) {
      writeOutput(JSON.stringify(result), g.output);
    } else {
      writeOutput(`Deleted ${fileId}`, g.output);
    }
  } catch (error) {
    handleError(error, g.json);
  }
}

async function runFileUrlCommand(fileId: string): Promise<void> {
  const g = globals();
  try {
    const client = getClient();
    const url = client.fileDownloadUrl(fileId);
    if (g.json) {
      writeOutput(JSON.stringify({ object: 'file_url', id: fileId, url }), g.output);
    } else {
      writeOutput(url, g.output);
    }
  } catch (error) {
    handleError(error, g.json);
  }
}

async function runJobListCommand(
  options: {
    limit?: string;
    type?: string;
    status?: string;
    doc?: string;
    documentId?: string;
  } = {},
): Promise<void> {
  const g = globals();
  try {
    const client = getClient();
    const jobs = await client.listJobs({
      limit: parseLimitOption(options.limit),
      type: options.type,
      status: options.status as never,
      documentId: options.doc ?? options.documentId,
    });
    writeOutput(
      formatJobList(jobs as unknown as { data?: Array<Record<string, unknown>> }, g.json),
      g.output,
    );
  } catch (error) {
    handleError(error, g.json);
  }
}

async function runJobGetCommand(jobId: string): Promise<void> {
  const g = globals();
  try {
    const client = getClient();
    const job = await client.getJob(jobId);
    writeJsonOrPretty(job, g.json, g.output);
  } catch (error) {
    handleError(error, g.json);
  }
}

async function runJobWaitCommand(id: string, options: { timeout?: string } = {}): Promise<void> {
  const g = globals();
  try {
    const client = getClient();
    const timeoutMs = parsePositiveSecondsOption(options.timeout, '--timeout') ?? 300_000;
    const job = await runCommandEffect(
      waitForJobEffect(client, id, { timeoutMs, onProgress: (line) => progress(line, g.quiet) }),
    );
    if (job) writeJsonOrPretty(job, g.json, g.output);
  } catch (error) {
    handleError(error, g.json);
  }
}

type DocumentWaitForOptions = {
  match?: string;
  timeout?: string;
  literal?: boolean;
};

function documentWaitForNextActions(
  documentId: string,
  matches: DocumentWaitForMatch[],
): Array<{ cmd: string; why: string }> {
  const firstPage = matches.find(
    (match) => typeof match.page === 'number' && Number.isFinite(match.page),
  )?.page;
  return [
    ...(firstPage !== undefined
      ? [
          {
            cmd: `okra documents read ${shellQuote(documentId)} --pages ${firstPage}`,
            why: 'Inspect the page containing the first matched node.',
          },
        ]
      : []),
    {
      cmd: `okra context get "<target context>" --source-id ${shellQuote(documentId)}`,
      why: 'Retrieve bounded cited context around the matched term.',
    },
  ];
}

async function runDocumentWaitForCommand(
  documentId: string,
  options: DocumentWaitForOptions = {},
): Promise<void> {
  const g = globals();
  try {
    const match = options.match?.trim();
    if (!match) {
      throw new OkraRuntimeError('INVALID_REQUEST', '--match is required', 400, {
        document_id: documentId,
      });
    }

    const client = getClient();
    const timeoutMs = parsePositiveSecondsOption(options.timeout, '--timeout') ?? 120_000;
    const result = await runCommandEffect(
      waitForDocumentMatchEffect(client, documentId, {
        match,
        literal: options.literal,
        timeoutMs,
        onProgress: (line) => progress(line, g.quiet),
      }),
    );
    if (!result) return;
    const envelope = {
      ok: true,
      command: 'documents wait-for',
      result,
      cost: { usd: null },
      citations: [],
      next_actions: documentWaitForNextActions(documentId, result.matched_nodes),
    };
    writeOutput(JSON.stringify(envelope, null, g.json ? 0 : 2), g.output);
  } catch (error) {
    handleError(error, g.json);
  }
}

type DocumentParseJobOptions = {
  engine?: string;
  model?: string;
  parser?: string;
  prompt?: string;
  server?: string;
  strategy?: string;
  wait?: boolean;
  waitTimeout?: string;
};

type DocumentVerifyOptions = {
  page?: string;
  bbox?: string;
};

function parserProfileRequest(profile: ParserProfile): ParserProfile {
  return {
    ...profile,
    ...(profile.params ? { params: { ...profile.params } } : {}),
    ...(profile.cost_hints ? { cost_hints: { ...profile.cost_hints } } : {}),
  };
}

type ParserProfileSelectionError = ParserProfileRegistryError | ParserProfileResolutionError;

function parserRegistryRuntimeError(): OkraRuntimeError {
  return new OkraRuntimeError(
    'INVALID_RESPONSE',
    'The built-in parser profile registry is invalid.',
    500,
  );
}

const executableParserProfileIdsEffect = Effect.fn('okrapdf/cli.executableParserProfileIds')(
  function* () {
    return yield* listExecutableParserProfilesEffect().pipe(
      Effect.map((profiles) => profiles.map((profile) => profile.id)),
      Effect.mapError(() => parserRegistryRuntimeError()),
    );
  },
);

function parserProfileSelectionFailureEffect(
  error: ParserProfileSelectionError,
  options: DocumentParseJobOptions,
  documentId: string,
): Effect.Effect<never, OkraRuntimeError> {
  if (error._tag === 'ParserProfileRegistryError') {
    return Effect.fail(parserRegistryRuntimeError());
  }

  if (error.reason === 'not_executable') {
    return executableParserProfileIdsEffect().pipe(
      Effect.flatMap((available) =>
        Effect.fail(
          new OkraRuntimeError(
            'NOT_IMPLEMENTED',
            `Parser profile ${error.profileId} is registered but not executable yet (no execution path honors prompt ${error.promptId}@${error.promptVersion}). Executable profiles: ${available.join(', ')}`,
            501,
            { parser_profile: error.profileId, prompt_id: error.promptId },
          ),
        ),
      ),
    );
  }

  if (options.model && options.prompt) {
    return Effect.fail(
      new OkraRuntimeError(
        'INVALID_REQUEST',
        `No built-in parser profile matches model ${options.model} with prompt ${options.prompt}`,
        400,
        { model: options.model, prompt: options.prompt },
      ),
    );
  }

  if (options.engine) {
    return executableParserProfileIdsEffect().pipe(
      Effect.flatMap((available) => {
        // The skill promises an invalid --engine returns `available_engines` + a
        // recovery path. The CLI validates the engine itself (so the request never
        // reaches the server's list), so enumerate the executable profiles here.
        const message = `engine '${options.engine}' is not available`;
        return Effect.fail(
          new OkraRuntimeError('INVALID_REQUEST', message, 400, {
            error: 'engine_not_available',
            message,
            engine: options.engine,
            available_engines: available,
            next_actions: [
              ...(available[0]
                ? [
                    {
                      cmd: `okra parse ${documentId} --engine ${available[0]}`,
                      why: 'Re-run with a supported engine (see available_engines).',
                    },
                  ]
                : []),
              {
                cmd: `okra parse ${documentId}`,
                why: 'Omit --engine for default routing — scanned docs auto-OCR.',
              },
            ],
          }),
        );
      }),
    );
  }

  return Effect.fail(
    new OkraRuntimeError('INVALID_RESPONSE', 'Parser profile resolution failed', 500),
  );
}

export const parseJobParserFieldsEffect = Effect.fn('okrapdf/cli.parseJobParserFields')(function* (
  options: DocumentParseJobOptions,
  documentId: string,
) {
  if (options.model || options.prompt) {
    if (!options.model || !options.prompt) {
      return yield* Effect.fail(
        new OkraRuntimeError(
          'INVALID_REQUEST',
          '`--model` and `--prompt` must be provided together',
          400,
        ),
      );
    }
    const profile = yield* resolveExecutableParserProfileByModelPromptEffect(
      options.model,
      options.prompt,
    ).pipe(
      Effect.catch((error) => parserProfileSelectionFailureEffect(error, options, documentId)),
    );
    return {
      parser: {
        id: profile.id,
        options: {
          model: options.model,
          prompt: options.prompt,
        },
      },
      parser_profile: parserProfileRequest(profile),
    };
  }

  if (options.engine) {
    const profile = yield* resolveExecutableParserProfileEffect(options.engine).pipe(
      Effect.catch((error) => parserProfileSelectionFailureEffect(error, options, documentId)),
    );
    return {
      parser: { id: profile.id },
      parser_profile: parserProfileRequest(profile),
    };
  }

  return {};
});

async function runDocumentParseJobCommand(
  documentId: string,
  options: DocumentParseJobOptions = {},
  allowLocalFile = false,
): Promise<void> {
  const g = globals();
  try {
    const localPath =
      allowLocalFile && existsSync(resolve(documentId))
        ? realpathSync(resolve(documentId))
        : undefined;
    if (localPath) {
      if (!localPath.toLowerCase().endsWith('.pdf')) {
        throw new OkraRuntimeError('INVALID_REQUEST', 'Local parse input must be a PDF file.', 400);
      }
      const configuredServer = options.server ?? process.env.OKRA_CLIENT_URL;
      if (!configuredServer && process.platform !== 'darwin') {
        throw new OkraRuntimeError(
          'INVALID_REQUEST',
          'Choose a processing server with --server or OKRA_CLIENT_URL.',
          400,
          {
            error: 'processing_server_required',
            retryable: false,
            next_actions: [
              {
                cmd: `okra parse ${JSON.stringify(localPath)} --server http://127.0.0.1:8787`,
                why: 'Parse through an existing local or self-hosted okra server.',
              },
              { cmd: 'okra serve', why: 'Start the local processing server.' },
            ],
          },
        );
      }
      const waitTimeoutSeconds =
        options.waitTimeout === undefined ? undefined : Number(options.waitTimeout);
      if (
        waitTimeoutSeconds !== undefined &&
        (!Number.isFinite(waitTimeoutSeconds) || waitTimeoutSeconds <= 0)
      ) {
        throw new OkraRuntimeError(
          'INVALID_REQUEST',
          '--wait-timeout must be a positive number of seconds.',
          400,
        );
      }
      const pdf = configuredServer ? readFileSync(localPath) : undefined;
      const snapshot = await runWorkbenchCommandEffect(
        Effect.gen(function* () {
          const endpoint = yield* resolveWorkbenchEndpointEffect(configuredServer);
          const result = yield* runWorkbenchParseEffect({
            endpoint,
            filename: localPath,
            ...(pdf ? { pdf } : { sourcePath: localPath }),
            parserId: options.parser ?? options.engine ?? 'text-layer',
            ...(options.model ? { model: options.model } : {}),
            wait: options.wait === true,
            ...(waitTimeoutSeconds === undefined
              ? {}
              : { waitTimeoutMs: waitTimeoutSeconds * 1_000 }),
          });
          return { result, endpoint };
        }),
      );
      if (snapshot === undefined) return;
      const { result, endpoint } = snapshot;
      if (g.json) {
        writeOutput(JSON.stringify(result), g.output);
      } else {
        writeOutput(
          [
            `${result.run.status === 'succeeded' ? 'Ready' : 'Started'}: ${result.run.id}`,
            `Document: ${result.document.id}`,
            `Server: ${endpoint.baseUrl}`,
          ].join('\n'),
          g.output,
        );
      }
      return;
    }

    const client = getClient();
    const parserFields = await Effect.runPromise(parseJobParserFieldsEffect(options, documentId));
    const job = await client.createJob({
      type: 'document.parse',
      document_id: documentId,
      ...(options.strategy ? { strategy: options.strategy } : {}),
      ...parserFields,
    });
    if (options.wait) {
      await runJobWaitCommand(job.id, { timeout: options.waitTimeout });
      return;
    }
    if (g.json) {
      writeOutput(JSON.stringify(job), g.output);
    } else {
      writeOutput(
        [
          `Queued: ${job.id}`,
          `Document: ${job.document_id ?? documentId}`,
          '',
          'Next:',
          `  okra jobs wait ${job.id}`,
          `  okra documents get ${job.document_id ?? documentId}`,
        ].join('\n'),
        g.output,
      );
    }
  } catch (error) {
    if (error instanceof DesktopEndpointError) {
      handleError(
        new OkraRuntimeError('HTTP_ERROR', error.message, error.retryable ? 503 : 400, {
          error: error.code,
          retryable: error.retryable,
          next_actions: [
            {
              cmd: 'open -a Okra',
              why: 'Open Okra.app and finish any required local model setup.',
            },
            {
              cmd: 'okra serve',
              why: 'Alternatively start the standalone processing server.',
            },
          ],
        }),
        g.json,
      );
      return;
    }
    if (error instanceof WorkbenchClientError) {
      handleError(
        new OkraRuntimeError(
          error.code === 'protocol_mismatch' ? 'INVALID_RESPONSE' : 'HTTP_ERROR',
          error.message,
          error.status ?? (error.retryable ? 503 : 400),
          {
            error: error.code,
            retryable: error.retryable,
            next_actions: [
              { cmd: 'okra serve', why: 'Start or inspect a local processing server.' },
            ],
          },
        ),
        g.json,
      );
      return;
    }
    handleError(error, g.json);
  }
}

async function runDocumentVerifyCommand(
  documentId: string,
  claim: string,
  options: DocumentVerifyOptions = {},
): Promise<void> {
  const g = globals();
  try {
    const client = getClient();
    const page = parseVerifyPageOption(options.page);
    const bbox = parseVerifyBboxOption(options.bbox);
    const result = await client.verify(documentId, { claim, page, ...(bbox ? { bbox } : {}) });
    const printable = { ...result, document_id: documentId, claim };
    if (g.json) {
      writeOutput(JSON.stringify(printable), g.output);
    } else {
      writeOutput(
        [
          `Verdict: ${result.verdict}`,
          `Document: ${documentId}`,
          `Page: ${result.page}`,
          `Evidence: ${result.evidence_snippet || '(none)'}`,
          `Confidence: ${result.confidence}`,
          `Model: ${result.model}`,
        ].join('\n'),
        g.output,
      );
    }
  } catch (error) {
    handleError(error, g.json);
  }
}

function parseVerifyPageOption(raw: string | undefined): number {
  const value = String(raw ?? '').trim();
  if (!/^[1-9]\d*$/.test(value)) {
    throw invalidVerifyRequest('--page must be a positive integer', {
      option: 'page',
      value: raw ?? null,
    });
  }
  return Number(value);
}

function parseVerifyBboxOption(
  raw: string | undefined,
): { x: number; y: number; w: number; h: number } | undefined {
  if (raw === undefined) return undefined;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw invalidVerifyRequest('--bbox must be valid JSON', { option: 'bbox', value: raw });
  }

  if (!isRecord(parsed)) {
    throw invalidVerifyRequest('--bbox must be an object with x, y, w, h', {
      option: 'bbox',
      value: raw,
    });
  }

  const { x, y, w, h } = parsed;
  if (
    typeof x === 'number' &&
    Number.isFinite(x) &&
    x >= 0 &&
    typeof y === 'number' &&
    Number.isFinite(y) &&
    y >= 0 &&
    typeof w === 'number' &&
    Number.isFinite(w) &&
    w > 0 &&
    typeof h === 'number' &&
    Number.isFinite(h) &&
    h > 0
  ) {
    return { x, y, w, h };
  }

  throw invalidVerifyRequest(
    '--bbox must be { "x": number, "y": number, "w": number, "h": number } with w/h > 0',
    {
      option: 'bbox',
      value: raw,
    },
  );
}

function invalidVerifyRequest(message: string, details: Record<string, unknown>): OkraRuntimeError {
  return new OkraRuntimeError('INVALID_REQUEST', message, 400, {
    error: 'invalid_request',
    message,
    details,
  });
}

async function runJobEventsCommand(jobId: string): Promise<void> {
  const g = globals();
  try {
    const client = getClient();
    const url = `${client.url}/v1/jobs/${encodeURIComponent(jobId)}/events`;
    if (g.json) {
      writeOutput(JSON.stringify({ object: 'job_events_url', id: jobId, url }), g.output);
    } else {
      writeOutput(url, g.output);
    }
  } catch (error) {
    handleError(error, g.json);
  }
}

async function runJobMutationCommand(
  jobId: string,
  action: 'cancel' | 'retry' | 'resume',
): Promise<void> {
  const g = globals();
  try {
    const client = getClient();
    const result = await client.request<unknown>(
      `/v1/jobs/${encodeURIComponent(jobId)}/${action}`,
      {
        method: 'POST',
      },
    );
    if (g.json) {
      writeOutput(JSON.stringify(result), g.output);
    } else {
      writeOutput(`${action}: ${jobId}`, g.output);
    }
  } catch (error) {
    handleError(error, g.json);
  }
}

async function runAgentsListCommand(): Promise<void> {
  const g = globals();
  try {
    const client = getClient();
    const agents = await client.request<unknown>('/v1/agents', { method: 'GET' });
    writeOutput(formatGenericList(agents, g.json), g.output);
  } catch (error) {
    handleError(error, g.json);
  }
}

async function runAgentGetCommand(agentId: string): Promise<void> {
  const g = globals();
  try {
    const client = getClient();
    const agent = await client.request<unknown>(`/v1/agents/${encodeURIComponent(agentId)}`, {
      method: 'GET',
    });
    writeJsonOrPretty(agent, g.json, g.output);
  } catch (error) {
    handleError(error, g.json);
  }
}

async function runAgentProfilesCommand(): Promise<void> {
  const g = globals();
  try {
    const client = getClient();
    const profiles = await client.request<unknown>('/v1/agents/profiles', { method: 'GET' });
    writeOutput(formatGenericList(profiles, g.json), g.output);
  } catch (error) {
    handleError(error, g.json);
  }
}

/**
 * `okra source list` — list configured source connectors (kind:"source").
 *
 * Filters `GET /v1/plugins` to `category:"sources"`. This is a self-host
 * (okra-solo) surface: point the CLI at a self-deployed instance with
 * `OKRA_BASE_URL` (or an `okra profile` whose base-url is the deploy), then the
 * existing Bearer auth (`OKRA_API_KEY`) authorizes the call. Not part of the
 * cloud `/v1/resources` catalog — connectors live on the self-host plugin
 * catalog (see internal/specs/okra-source-connectors.md).
 */
async function runSourceListCommand(): Promise<void> {
  const g = globals();
  try {
    const client = getClient();
    const result = await client.request<{ plugins?: Array<Record<string, unknown>> }>(
      '/v1/plugins',
      {
        method: 'GET',
      },
    );
    const sources = (result.plugins ?? []).filter((p) => p.category === 'sources');
    if (g.json) {
      writeOutput(JSON.stringify({ object: 'list', data: sources }), g.output);
    } else {
      writeOutput(formatGenericList({ data: sources }, g.json), g.output);
    }
  } catch (error) {
    handleError(error, g.json);
  }
}

/**
 * `okra source sync <id> [--parser <id>] [--limit <n>]` — run a source-connector
 * sync, importing new PDFs from the connector into the instance and kicking off
 * parse runs. POSTs to the self-host route `/v1/plugins/:id/sync`. Response:
 * `{ imported:[{documentId,externalId,fileName}], skipped, cursor }`.
 */
async function runSourceSyncCommand(
  id: string,
  options: { parser?: string; limit?: string },
): Promise<void> {
  const g = globals();
  try {
    const body: { parserId?: string; limit?: number } = {};
    if (options.parser) body.parserId = options.parser;
    if (options.limit !== undefined) body.limit = parsePositiveInt(options.limit, '--limit', 25);

    const client = getClient();
    const result = await client.request<{
      imported?: unknown[];
      skipped?: number;
      cursor?: string;
    }>(`/v1/plugins/${encodeURIComponent(id)}/sync`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (g.json) {
      writeOutput(JSON.stringify(result), g.output);
    } else {
      const importedCount = Array.isArray(result.imported) ? result.imported.length : 0;
      writeOutput(
        `Synced ${id}: imported ${importedCount}, skipped ${result.skipped ?? 0}`,
        g.output,
      );
    }
  } catch (error) {
    handleError(error, g.json);
  }
}

async function runWorkflowCatalogCommand(): Promise<void> {
  const g = globals();
  try {
    const client = getClient();
    const catalog = await client.request<unknown>('/v1/workflows/catalog', { method: 'GET' });
    if (g.json) {
      writeOutput(JSON.stringify(catalog), g.output);
    } else {
      writeOutput(formatGenericList(catalog, g.json), g.output);
    }
  } catch (error) {
    handleError(error, g.json);
  }
}

function registerUploadCommand(commandName: string, description: string): void {
  program
    .command(`${commandName} <source>`)
    .description(description)
    .summary('Upload a PDF and wait for processing')
    .option('--no-wait', "Fire-and-forget (don't wait for processing)")
    .option('--wait-timeout <seconds>', 'Maximum seconds to wait for processing')
    .option(
      '--vendor-options <json>',
      'JSON vendor-specific options (e.g., \'{"model":"gemini-3.1-pro","parse_mode":"parse_page_with_agent"}\')',
    )
    .action(async (source, options) => {
      await runUploadCommand(source, options);
    });
}

registerUploadCommand(
  'upload',
  "Upload a PDF (file path, URL, or '-' for stdin), wait for processing",
);

interface PluginScaffoldCommandOptions {
  root?: string;
  force?: boolean;
  generate?: boolean;
}

function runPluginScaffoldCommand(
  role: string,
  name: string,
  options: PluginScaffoldCommandOptions,
): void {
  const g = globals();
  try {
    const result = scaffoldPlugin(role, name, {
      cwd: options.root ?? process.cwd(),
      force: options.force === true,
      generate: options.generate !== false,
    });
    writeOutput(g.json ? JSON.stringify(result) : formatScaffoldPluginResult(result), g.output);
  } catch (error) {
    handleError(error, g.json);
  }
}

// ============================================================================
// add command — ecosystem descriptor/package scaffolder (M-CAT #856)
// ============================================================================
program
  .command('add <role> <name>', { hidden: true })
  .description('Scaffold an ecosystem plugin descriptor and role package')
  .option('--root <dir>', 'okra monorepo root (defaults to walking up from cwd)')
  .option('--force', 'Overwrite existing scaffold files')
  .option('--no-generate', 'Skip regenerating ecosystem catalog descriptor output')
  .action(runPluginScaffoldCommand);

// ============================================================================
// doctor command — structured CLI/environment diagnostics
// ============================================================================
program
  .command('doctor')
  .description('Run CLI, config, auth, and API diagnostics')
  .summary('Diagnose CLI setup')
  .option('--base-url <url>', 'Override API base URL for diagnostic probes')
  .action(async (options: { baseUrl?: string }) => {
    try {
      await runDoctorCommand(options);
    } catch (error) {
      handleError(error, globals().json);
    }
  });

// ============================================================================
// resources command — discover top-level API nouns and action verbs
// ============================================================================
const resourcesCmd = program
  .command('resources')
  .alias('resource')
  .description('Discover okraPDF API resources and actions')
  .summary('List API resources')
  .action(runResourceListCommand);

resourcesCmd
  .command('list')
  .alias('ls')
  .description('List top-level API resources and action verbs')
  .action(runResourceListCommand);

resourcesCmd
  .command('show <name>')
  .alias('get')
  .description('Show a resource or action contract')
  .action(runResourceShowCommand);

// ============================================================================
// content-types command — typed extraction contracts (#330/#331)
// ============================================================================
const contentTypesCmd = program
  .command('content-types')
  .alias('content-type')
  .alias('ct')
  .description('Discover typed extraction content types (schema + evidence + lifecycle)')
  .summary('List content types')
  .action(runContentTypesListCommand);

contentTypesCmd
  .command('list')
  .alias('ls')
  .description('List registered content types')
  .action(runContentTypesListCommand);

contentTypesCmd
  .command('show <id>')
  .alias('get')
  .description('Show a content-type manifest (schema, fields, evidence policy)')
  .action(runContentTypesShowCommand);

// ============================================================================
// plugin command — ecosystem catalog rows (M-CAT)
// ============================================================================
const pluginCmd = program
  .command('plugin')
  .alias('plugins')
  .description('Browse okra plugins from the ecosystem catalog')
  .summary('List plugins')
  .option('--role <role>', 'Filter by catalog role (all|parser|source|integration)')
  .option('--status <status>', 'Filter by catalog status (all|supported|recipe|planned)')
  .option('--source <source>', 'Filter by row source (all|descriptor|legacy_surface)')
  .action((options: { role?: string; status?: string; source?: string }) => {
    const g = globals();
    try {
      const rows = listPluginCatalog(options);
      writeOutput(
        g.json ? JSON.stringify({ plugins: rows }) : formatPluginCatalogList(rows),
        g.output,
      );
    } catch (error) {
      handleError(error, g.json);
    }
  });

pluginCmd
  .command('list')
  .alias('ls')
  .description('List plugin catalog rows')
  .option('--role <role>', 'Filter by catalog role (all|parser|source|integration)')
  .option('--status <status>', 'Filter by catalog status (all|supported|recipe|planned)')
  .option('--source <source>', 'Filter by row source (all|descriptor|legacy_surface)')
  .action((options: { role?: string; status?: string; source?: string }) => {
    const g = globals();
    try {
      const rows = listPluginCatalog(options);
      writeOutput(
        g.json ? JSON.stringify({ plugins: rows }) : formatPluginCatalogList(rows),
        g.output,
      );
    } catch (error) {
      handleError(error, g.json);
    }
  });

pluginCmd
  .command('add <role> <name>')
  .description('Scaffold a plugin descriptor and role package')
  .option('--root <dir>', 'okra monorepo root (defaults to walking up from cwd)')
  .option('--force', 'Overwrite existing scaffold files')
  .option('--no-generate', 'Skip regenerating ecosystem catalog descriptor output')
  .action(runPluginScaffoldCommand);

pluginCmd
  .command('info <id>')
  .alias('show')
  .alias('get')
  .description('Show plugin catalog metadata')
  .action((id: string) => {
    const g = globals();
    try {
      const row = getPluginCatalogInfo(id);
      writeOutput(g.json ? JSON.stringify(row) : formatPluginCatalogInfo(row), g.output);
    } catch (error) {
      handleError(error, g.json);
    }
  });

pluginCmd
  .command('run <id>')
  .alias('invoke')
  .description('Run a plugin row through the available runtime bridge')
  .option('-p, --payload <json>', 'Inline JSON payload', '{}')
  .option('-f, --payload-file <path>', 'Read payload from a JSON file')
  .action(async (id: string, options: { payload: string; payloadFile?: string }) => {
    const g = globals();
    try {
      const input = options.payloadFile
        ? parseInlineJsonArg(readFileSync(options.payloadFile, 'utf8'), '--payload-file')
        : parseInlineJsonArg(options.payload, '--payload');
      const result = await runPluginCatalog(id, input, (legacyId, payload) =>
        invokeFacet(getFacetCtx(), legacyId, input),
      );
      writeOutput(g.json ? JSON.stringify(result) : formatPluginCatalogRunResult(result), g.output);
    } catch (error) {
      handleError(error, g.json);
    }
  });

for (const ct of listContentTypes()) {
  const manifest = getContentType(ct.id);
  if (!manifest) {
    throw new Error(`Registered content type "${ct.id}" could not be resolved.`);
  }

  const noun = program
    .command(ct.cli_noun)
    .description(`${ct.label} extraction commands for content type "${ct.id}"`)
    .summary(`Extract ${ct.label.toLowerCase()} data`)
    .action(() => {
      noun.help();
    });

  for (const alias of manifest.cli.aliases ?? []) {
    noun.alias(alias);
  }

  noun
    .command('extract <source>')
    .description(`Equivalent to "extract <source> --content-type ${ct.id}"`)
    .option('--no-wait', 'Fire-and-forget')
    .option('--prompt <query>', 'Extraction prompt')
    .option('--cite', 'Return per-field source citations (page + bbox)')
    .action(
      (
        source: string,
        options: {
          wait?: boolean;
          prompt?: string;
          cite?: boolean;
        },
      ) => runExtractCommand(source, { ...options, contentType: ct.id }),
    );
}

// ============================================================================
// documents command — processed document resource
// ============================================================================
const documentsCmd = program
  .command('documents')
  .alias('document')
  .alias('docs')
  .description('Document resource operations')
  .summary('Work with processed documents')
  .action(runDocumentListCommand);

documentsCmd
  .command('list')
  .alias('ls')
  .description('List processed documents')
  .action(runDocumentListCommand);

documentsCmd
  .command('upload <source>')
  .description('Upload a PDF into the document lifecycle')
  .option('--no-wait', "Fire-and-forget (don't wait for processing)")
  .option('--wait-timeout <seconds>', 'Maximum seconds to wait for processing')
  .option('--vendor-options <json>', 'JSON vendor-specific options')
  .action(async (source, options) => {
    await runUploadCommand(source, options);
  });

documentsCmd
  .command('get <docId>')
  .alias('status')
  .description('Get document status and metadata')
  .action(runDocumentStatusCommand);

documentsCmd
  .command('read <docId>')
  .description('Read processed document markdown')
  .option('--pages <range>', 'Page range, e.g. 1-5')
  .action(runDocumentReadCommand);

documentsCmd
  .command('wait-for <docId>')
  .description('Wait until a matching document node is ingested')
  .requiredOption('--match <term>', 'Term or FTS expression to wait for')
  .option('--timeout <seconds>', 'Maximum seconds to wait', '120')
  .option('--literal', 'Use literal substring matching instead of FTS')
  .action(runDocumentWaitForCommand);

documentsCmd
  .command('urls <docId>')
  .alias('url')
  .description('Print deterministic document API URLs')
  .action(runDocumentUrlsCommand);

documentsCmd
  .command('reparse <docId>')
  .description('Create a document.parse job for an existing document')
  .option('--model <model>', 'Parser model id')
  .option('--prompt <prompt>', 'Parser prompt id or id@version')
  .option('--engine <engine>', 'Deprecated parser engine alias; use --model/--prompt')
  .option('--strategy <strategy>', 'Parser strategy/vendor alias')
  .option('--wait', 'Wait for the parse job to finish')
  .option('--wait-timeout <seconds>', 'Maximum seconds to wait when --wait is set')
  .action(runDocumentParseJobCommand);

documentsCmd
  .command('verify <docId> <claim>')
  .description('Verify a claim against a page using the vision model')
  .requiredOption('--page <n>', 'Page number (1-based) to verify against')
  .option('--bbox <json>', 'Optional region {"x":..,"y":..,"w":..,"h":..} to focus on')
  .action(runDocumentVerifyCommand);

documentsCmd
  .command('delete <docId>')
  .alias('rm')
  .description('Delete a document')
  .action(runDocumentDeleteCommand);

// ============================================================================
// files command — passive file resource
// ============================================================================
const filesCmd = program
  .command('files')
  .alias('file')
  .description('Passive file resource operations')
  .summary('Work with uploaded file assets')
  .action(runFileListCommand);

filesCmd
  .command('list')
  .alias('ls')
  .description('List passive file assets')
  .option('--limit <n>', 'Max files to return')
  .option('--cursor <cursor>', 'Pagination cursor')
  .action(runFileListCommand);

filesCmd
  .command('upload <source>')
  .description('Upload a passive file asset without creating a document')
  .option('--file-name <name>', 'Override file name')
  .option('--transport <mode>', 'auto | multipart | direct', 'auto')
  .action(runFileUploadCommand);

filesCmd.command('get <fileId>').description('Get file metadata').action(runFileGetCommand);

filesCmd.command('url <fileId>').description('Print the file bytes URL').action(runFileUrlCommand);

filesCmd
  .command('delete <fileId>')
  .alias('rm')
  .description('Delete a passive file asset')
  .action(runFileDeleteCommand);

// ============================================================================
// jobs command — long-running work resource
// ============================================================================
const jobsCmd = program
  .command('jobs')
  .alias('job')
  .description('Job resource operations')
  .summary('Inspect parse/render/workflow jobs')
  .action(runJobListCommand);

jobsCmd
  .command('list')
  .alias('ls')
  .description('List jobs')
  .option('--limit <n>', 'Max jobs to return')
  .option('--type <type>', 'Filter by job type')
  .option('--status <status>', 'Filter by job status')
  .option('--doc <docId>', 'Filter by document ID')
  .action(runJobListCommand);

jobsCmd
  .command('get <jobId>')
  .alias('show')
  .description('Get job status and result')
  .action(runJobGetCommand);

jobsCmd
  .command('wait <jobOrDocId>')
  .description('Wait for a job, or for the latest document.parse job for a document ID')
  .option('--timeout <seconds>', 'Maximum seconds to wait', '300')
  .action(runJobWaitCommand);

jobsCmd
  .command('events <jobId>')
  .description('Print the job event stream URL')
  .action(runJobEventsCommand);

jobsCmd
  .command('cancel <jobId>')
  .description('Cancel a queued or running job')
  .action((jobId: string) => runJobMutationCommand(jobId, 'cancel'));

jobsCmd
  .command('retry <jobId>')
  .description('Retry a failed job')
  .action((jobId: string) => runJobMutationCommand(jobId, 'retry'));

jobsCmd
  .command('resume <jobId>')
  .description('Resume a paused job')
  .action((jobId: string) => runJobMutationCommand(jobId, 'resume'));

// ============================================================================
// agents command — registered agent resource
// ============================================================================
const agentsCmd = program
  .command('agents')
  .alias('agent')
  .description('Agent resource operations')
  .summary('Inspect registered agents')
  .action(runAgentsListCommand);

agentsCmd
  .command('list')
  .alias('ls')
  .description('List registered agents')
  .action(runAgentsListCommand);

agentsCmd.command('get <agentId>').description('Get agent details').action(runAgentGetCommand);

agentsCmd
  .command('profiles')
  .description('List built-in runtime profiles')
  .action(runAgentProfilesCommand);

// ============================================================================
// source command — source-connector plugins (kind:"source"), self-host (okra-solo)
//
// Pull PDFs out of where they already live (Google Drive, Gmail) into a
// self-deployed okra instance. Targets the self-host plugin catalog, so point
// the CLI at the deploy via `OKRA_BASE_URL` / `okra profile … --base-url`; the
// existing `OKRA_API_KEY` Bearer auth authorizes the call. Enable a connector
// with the generic plugin enable flow first. Spec:
// internal/specs/okra-source-connectors.md.
// ============================================================================
const sourceCmd = program
  .command('source')
  .alias('sources')
  .description('Source-connector operations (self-host: import PDFs from Drive/Gmail)')
  .summary('Sync PDFs from source connectors')
  .action(runSourceListCommand);

sourceCmd
  .command('list')
  .alias('ls')
  .description('List configured source connectors')
  .action(runSourceListCommand);

sourceCmd
  .command('sync <id>')
  .description('Import new PDFs from a source connector and start parse runs')
  .option('--parser <id>', 'Parser plugin id to use (defaults to the instance default)')
  .option('--limit <n>', 'Max documents to import this run (default 25)')
  .action((id: string, options: { parser?: string; limit?: string }) =>
    runSourceSyncCommand(id, options),
  );

const contextDeps = { globals, getClient };
registerContextCommand(program, contextDeps);

// ============================================================================
// workflows command — research/eval workflow contracts
// ============================================================================
function loadWorkflowDefinition(source: string): DynamicWorkflowDefinition {
  const trimmed = source.trim();
  const raw = trimmed.startsWith('{') ? trimmed : readFileSync(trimmed, 'utf8');
  return JSON.parse(raw) as DynamicWorkflowDefinition;
}

function parseWorkflowDatasetRef(raw: string | undefined): DynamicWorkflowDatasetRef | undefined {
  if (!raw) return undefined;
  const value = raw.trim();
  if (!value) return undefined;

  if (value.startsWith('hf://')) {
    const withoutScheme = value.slice('hf://'.length);
    const [id, query = ''] = withoutScheme.split('?');
    const params = new URLSearchParams(query);
    return {
      provider: 'huggingface',
      id,
      split: params.get('split') || undefined,
      revision: params.get('revision') || undefined,
    };
  }

  if (value.startsWith('okra://')) {
    return { provider: 'okra', id: value.slice('okra://'.length) };
  }

  if (/^https?:\/\//i.test(value)) {
    return { provider: 'url', id: value, sourceUrl: value };
  }

  return { provider: 'local', id: value };
}

/**
 * Parse an inline JSON CLI argument into a structured envelope on failure. A bare
 * JSON.parse on a user-supplied `--flag '<json>'` throws a SyntaxError that
 * handleError renders as the generic error:"error", code:1 dead-end (no stable
 * code, no recovery) — same class as the redact --policy / workflows-validate
 * fixes. Returns the parsed value (any JSON type).
 */
export function parseInlineJsonArg(raw: string, flagName: string): unknown {
  try {
    return JSON.parse(raw);
  } catch (error) {
    const message = `${flagName} must be valid JSON: ${error instanceof Error ? error.message : String(error)}`;
    throw new OkraRuntimeError('INVALID_REQUEST', message, 400, {
      error: 'invalid_json_argument',
      message,
      next_actions: [
        { cmd: `${flagName} '{"key":"value"}'`, why: `Pass ${flagName} as valid JSON.` },
      ],
    });
  }
}

export function parseOptionalJsonObject(
  raw: string | undefined,
  flagName: string,
): Record<string, unknown> | undefined {
  if (!raw) return undefined;
  const parsed = parseInlineJsonArg(raw, flagName);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    const message = `${flagName} must be a JSON object`;
    throw new OkraRuntimeError('INVALID_REQUEST', message, 400, {
      error: 'invalid_json_argument',
      message,
      next_actions: [
        { cmd: `${flagName} '{"key":"value"}'`, why: `Pass ${flagName} as a JSON object.` },
      ],
    });
  }
  return parsed as Record<string, unknown>;
}

function formatWorkflowValidation(definition: DynamicWorkflowDefinition, asJson: boolean): string {
  const validation = validateDynamicWorkflow(definition);
  const capabilities = validation.ok ? dynamicWorkflowToCapabilities(definition) : null;
  if (asJson) {
    return JSON.stringify({ validation, capabilities }, null, 2);
  }

  const lines = [
    `Workflow: ${definition.id || '(missing id)'}`,
    `Status: ${validation.ok ? 'valid' : 'invalid'}`,
    `Steps: ocr=${validation.stepKinds.ocr}, gate=${validation.stepKinds.gate}, code=${validation.stepKinds.code}, vlm=${validation.stepKinds.vlm}`,
  ];
  if (definition.eval?.dataset) {
    lines.push(`Dataset: ${definition.eval.dataset.provider}:${definition.eval.dataset.id}`);
  }
  if (validation.errors.length) {
    lines.push('', 'Errors:', ...validation.errors.map((error) => `  - ${error}`));
  }
  if (validation.warnings.length) {
    lines.push('', 'Warnings:', ...validation.warnings.map((warning) => `  - ${warning}`));
  }
  return lines.join('\n');
}

function formatWorkflowStepsGuide(asJson: boolean): string {
  const guide = createDynamicWorkflowBuildStepsGuide();
  if (asJson) return JSON.stringify(guide, null, 2);

  const lines = [
    'Dynamic workflow build steps',
    '',
    `Execution: ${guide.execution_model.unit}`,
    `Order: ${guide.execution_model.step_order}`,
    `Runtime: ${guide.execution_model.hosted_runtime}`,
    `Versioning: ${guide.execution_model.versioning}`,
    '',
    'Primitives:',
  ];

  for (const primitive of guide.primitives) {
    lines.push(`  ${primitive.kind}: ${primitive.purpose}`);
    lines.push(`    required: ${primitive.required.join(', ')}`);
    lines.push(`    optional: ${primitive.optional.join(', ')}`);
  }

  lines.push('', 'Usage:', ...guide.usage.map((cmd) => `  ${cmd}`));
  return lines.join('\n');
}

function formatWorkflowBuild(
  build: ReturnType<typeof buildDynamicWorkflowDefinition>,
  asJson: boolean,
  sourceOnly = false,
): string {
  if (sourceOnly) {
    return build.agent_workflow_source ?? build.validation.errors.join('\n');
  }
  if (asJson) return JSON.stringify(build, null, 2);

  const lines = [
    `Workflow: ${build.name || build.workflow_id || '(missing id)'}`,
    `Status: ${build.validation.ok ? 'valid' : 'invalid'}`,
    `Steps: ${build.step_graph.map((step) => `${step.id}:${step.kind}`).join(', ') || '(none)'}`,
    `Build: ${build.agent_workflow_source ? 'agent_workflow_source' : 'not emitted'}`,
  ];
  if (build.validation.errors.length) {
    lines.push('', 'Errors:', ...build.validation.errors.map((error) => `  - ${error}`));
  }
  if (build.validation.warnings.length) {
    lines.push('', 'Warnings:', ...build.validation.warnings.map((warning) => `  - ${warning}`));
  }
  if (build.agent_workflow_source) {
    lines.push(
      '',
      'Next:',
      `  okra workflows run ${build.source_path ?? './workflow.json'} --dry-run --json`,
    );
  }
  return lines.join('\n');
}

const workflowCmd = program
  .command('workflows')
  .alias('workflow')
  .description('Validate and run composable OCR/gate/code/VLM eval workflows');
workflowCmd.summary('Run dataset-backed workflow evals');

workflowCmd
  .command('catalog')
  .alias('list')
  .description('List hosted workflow templates and capabilities')
  .action(runWorkflowCatalogCommand);

workflowCmd
  .command('steps')
  .description('Show the OCR/gate/code/VLM step primitives used to build workflows')
  .action(() => {
    const g = globals();
    try {
      writeOutput(formatWorkflowStepsGuide(!!g.json), g.output);
    } catch (error) {
      handleError(error, g.json);
    }
  });

workflowCmd
  .command('examples')
  .description('List built-in research workflow examples')
  .action(() => {
    const g = globals();
    const examples = listDynamicWorkflowExamples();
    if (g.json) {
      writeOutput(
        JSON.stringify(
          examples.map(({ definition, ...example }) => ({
            ...example,
            workflowId: definition.id,
          })),
        ),
        g.output,
      );
      return;
    }
    writeOutput(examples.map((example) => `${example.id}\t${example.title}`).join('\n'), g.output);
  });

workflowCmd
  .command('example <id>')
  .description('Print a built-in research workflow definition as JSON')
  .action((id: string) => {
    const g = globals();
    const example = getDynamicWorkflowExample(id);
    if (!example) {
      handleError(
        new OkraRuntimeError('INVALID_REQUEST', `Unknown workflow example "${id}".`, 400, {
          error: 'not_found',
          message: `Unknown workflow example "${id}".`,
          next_actions: [
            { cmd: 'okra workflow examples', why: 'List the available workflow example ids.' },
          ],
        }),
        g.json,
      );
    }
    writeOutput(JSON.stringify(example.definition, null, 2), g.output);
  });

workflowCmd
  .command('validate <file>')
  .description('Validate a workflow definition JSON file')
  .action((file: string) => {
    const g = globals();
    try {
      const definition = loadWorkflowDefinition(file);
      const validation = validateDynamicWorkflow(definition);
      writeOutput(formatWorkflowValidation(definition, !!g.json), g.output);
      if (!validation.ok) process.exitCode = 1;
    } catch (error) {
      handleError(error, g.json);
    }
  });

workflowCmd
  .command('build <file>')
  .description('Validate and compile a step workflow into hosted agent-workflow source')
  .option('--source-only', 'Print only the compiled hosted workflow source')
  .action((file: string, options: { sourceOnly?: boolean }) => {
    const g = globals();
    try {
      const definition = loadWorkflowDefinition(file);
      const build = buildDynamicWorkflowDefinition(definition, { sourcePath: file });
      const output = formatWorkflowBuild(build, !!g.json, options.sourceOnly === true);
      writeOutput(output, g.output);
      if (!build.validation.ok) process.exitCode = 1;
    } catch (error) {
      handleError(error, g.json);
    }
  });

workflowCmd
  .command('run <file>')
  .description('Create a hosted workflow/eval run from a workflow definition')
  .option(
    '--dataset <ref>',
    'Override eval dataset ref (hf://org/name?split=train, okra://id, URL, or local id)',
  )
  .option('--dry-run', 'Ask the API to validate/plan without executing')
  .option('--metadata <json>', 'Attach run metadata JSON object')
  .action(
    async (
      file: string,
      options: {
        dataset?: string;
        dryRun?: boolean;
        metadata?: string;
      },
    ) => {
      const g = globals();
      try {
        const client = getClient();
        const definition = loadWorkflowDefinition(file);
        const result = await client.workflows.run(definition, {
          dataset: parseWorkflowDatasetRef(options.dataset),
          dryRun: options.dryRun,
          metadata: parseOptionalJsonObject(options.metadata, '--metadata'),
        });

        if (g.json) {
          writeOutput(JSON.stringify(result), g.output);
          return;
        }

        writeOutput(
          [
            `Run: ${result.id}`,
            `Status: ${result.status}`,
            `Workflow: ${result.workflowId}`,
            result.evalRunId ? `Eval run: ${result.evalRunId}` : null,
            result.eventsUrl ? `Events: ${result.eventsUrl}` : null,
            result.resultsUrl ? `Results: ${result.resultsUrl}` : null,
          ]
            .filter(Boolean)
            .join('\n'),
          g.output,
        );
      } catch (error) {
        handleError(error, g.json);
      }
    },
  );

// ============================================================================
// components command — declarative TSX lowered to the shared workflow contract
// ============================================================================
const componentCmd = program
  .command('components')
  .alias('component')
  .description('Build and run declarative TSX structured-output Components');
componentCmd.summary('Run TSX Components through any Okra server');

componentCmd
  .command('build <file>')
  .description('Compile a TSX Component into schemas, assertions, evals, and a workflow')
  .action(async (file: string) => {
    const g = globals();
    try {
      const component = await loadComponentFile(file);
      writeOutput(JSON.stringify(compileComponent(component), null, 2), g.output);
    } catch (error) {
      handleError(error, g.json);
    }
  });

componentCmd
  .command('run <file>')
  .description('Compile a TSX Component and run it through the configured Okra server')
  .option('--document <id>', 'Document id used as the Component input')
  .option(
    '--dataset <ref>',
    'Override eval dataset ref (hf://org/name?split=train, okra://id, URL, or local id)',
  )
  .option('--dry-run', 'Ask the server to validate and plan without executing')
  .option('--metadata <json>', 'Attach run metadata JSON object')
  .action(
    async (
      file: string,
      options: {
        document?: string;
        dataset?: string;
        dryRun?: boolean;
        metadata?: string;
      },
    ) => {
      const g = globals();
      try {
        const component = await loadComponentFile(file);
        const client = getClient();
        const result = await client.components.run(component, {
          dataset: parseWorkflowDatasetRef(options.dataset),
          ...(options.document ? { inputs: { document_id: options.document } } : {}),
          dryRun: options.dryRun,
          metadata: parseOptionalJsonObject(options.metadata, '--metadata'),
        });

        if (g.json) {
          writeOutput(JSON.stringify(result), g.output);
          return;
        }
        writeOutput(
          [
            `Run: ${result.id}`,
            `Status: ${result.status}`,
            `Workflow: ${result.workflowId}`,
            result.eventsUrl ? `Events: ${result.eventsUrl}` : null,
            result.resultsUrl ? `Results: ${result.resultsUrl}` : null,
          ]
            .filter(Boolean)
            .join('\n'),
          g.output,
        );
      } catch (error) {
        handleError(error, g.json);
      }
    },
  );

// ============================================================================
// parse/audit/redact commands — product workflow verbs over /v1/workflows
// ============================================================================
async function runDocumentWorkflowCommand(
  kind: DocumentWorkflowKind,
  documentId: string,
  options: {
    recipe?: string;
    engine?: string;
    standard?: string;
    model?: string;
    policy?: string;
  },
): Promise<void> {
  const g = globals();
  try {
    const client = getClient();
    const result = await runDocumentWorkflow(client, kind, {
      documentId,
      recipeId: options.recipe,
      engine: options.engine,
      standard: options.standard,
      model: options.model,
      policy: parsePolicyJson(options.policy),
    });

    writeOutput(g.json ? JSON.stringify(result) : formatDocumentWorkflowRun(result), g.output);
  } catch (error) {
    // The cloud /v1/workflows endpoint only accepts dynamic-workflow JS modules
    // today; recipe runs (audit/redact) execute on the self-host runtime.
    // Translate the baffling upstream 400 into an honest, actionable envelope
    // instead of leaking "code must be a non-empty JavaScript module string".
    if (error instanceof Error && error.message.includes('JavaScript module string')) {
      writeOutput(
        JSON.stringify(
          createFailureEnvelope(
            kind,
            'not_supported_on_cloud',
            `okra ${kind} runs recipe workflows on the self-host runtime; the cloud API does not execute workflow recipes yet.`,
            [
              {
                cmd: 'okra serve',
                why: 'Start the self-host runtime, which executes recipe workflows.',
              },
              {
                cmd: `okra profile use <self-host-profile> && okra ${kind} ${shellQuote(documentId)}`,
                why: 'Re-run against a self-host profile.',
              },
              ...(kind === 'redact'
                ? [
                    {
                      cmd: `okra resources show documents`,
                      why: 'Cloud alternative: POST /v1/documents/{id}/redact applies bbox region redactions directly.',
                    },
                  ]
                : []),
            ],
          ),
        ),
        g.output,
      );
      process.exitCode = 1;
      return;
    }
    handleError(error, g.json);
  }
}

interface WorkbenchServerOptions {
  server?: string;
}

interface WorkbenchRunOptions extends WorkbenchServerOptions {
  wait?: boolean;
  waitTimeout?: string;
  artifacts?: boolean;
  events?: boolean;
}

const resolveWorkbenchEndpointEffect = (server?: string) =>
  Effect.suspend<WorkbenchEndpoint, DesktopDiscoveryError | OkraRuntimeError, never>(() => {
    const configuredServer = server ?? process.env.OKRA_CLIENT_URL;
    if (configuredServer) {
      return Effect.succeed({
        baseUrl: configuredServer,
        ...(process.env.OKRA_API_KEY ? { token: process.env.OKRA_API_KEY } : {}),
      });
    }
    if (process.platform === 'darwin') return discoverDesktopEndpointEffect();
    return Effect.fail(
      new OkraRuntimeError(
        'INVALID_REQUEST',
        'Choose a processing server with --server or OKRA_CLIENT_URL.',
        400,
        {
          error: 'processing_server_required',
          retryable: false,
          next_actions: [
            {
              cmd: 'okra runs --server http://127.0.0.1:8787',
              why: 'Attach to an existing local or self-hosted okra server.',
            },
            { cmd: 'okra serve', why: 'Start the standalone processing server.' },
          ],
        },
      ),
    );
  });

function handleWorkbenchCommandError(error: unknown, json: boolean | undefined): void {
  if (error instanceof DesktopEndpointError) {
    handleError(
      new OkraRuntimeError('HTTP_ERROR', error.message, error.retryable ? 503 : 400, {
        error: error.code,
        retryable: error.retryable,
        next_actions: [
          {
            cmd: 'open -a Okra',
            why: 'Open Okra.app and finish any required local model setup.',
          },
          {
            cmd: 'okra serve',
            why: 'Alternatively start the standalone processing server.',
          },
        ],
      }),
      json,
    );
    return;
  }
  if (error instanceof WorkbenchClientError) {
    handleError(
      new OkraRuntimeError(
        error.code === 'protocol_mismatch' ? 'INVALID_RESPONSE' : 'HTTP_ERROR',
        error.message,
        error.status ?? (error.retryable ? 503 : 400),
        {
          error: error.code,
          retryable: error.retryable,
          next_actions: [{ cmd: 'okra serve', why: 'Start or inspect a local processing server.' }],
        },
      ),
      json,
    );
    return;
  }
  handleError(error, json);
}

function workbenchWaitTimeoutMs(raw: string | undefined): number | undefined {
  if (raw === undefined) return undefined;
  const seconds = Number(raw);
  if (!Number.isFinite(seconds) || seconds <= 0) {
    throw new OkraRuntimeError(
      'INVALID_REQUEST',
      '--wait-timeout must be a positive number of seconds.',
      400,
    );
  }
  return seconds * 1_000;
}

function runWorkbenchCommandEffect<A, E>(program: WorkbenchProgram<A, E>): Promise<A | undefined> {
  return runCommandEffect(
    withWorkbenchTransport(program).pipe(
      Effect.mapError((error) =>
        error instanceof WorkbenchOperationError
          ? workbenchErrorCause(error)
          : error instanceof DesktopDiscoveryError
            ? desktopDiscoveryErrorCause(error)
            : error,
      ),
    ),
  );
}

async function runWorkbenchRunsCommand(options: WorkbenchServerOptions): Promise<void> {
  const g = globals();
  try {
    const runs = await runWorkbenchCommandEffect(
      resolveWorkbenchEndpointEffect(options.server).pipe(Effect.flatMap(listWorkbenchRunsEffect)),
    );
    if (runs === undefined) return;
    if (g.json) {
      writeOutput(JSON.stringify({ object: 'client_run_list', data: runs }), g.output);
      return;
    }
    writeOutput(
      runs.length === 0
        ? 'No processing runs.'
        : runs
            .map((run) => `${run.id}  ${run.status}  ${run.documentId}  ${run.parserId}`)
            .join('\n'),
      g.output,
    );
  } catch (error) {
    handleWorkbenchCommandError(error, g.json);
  }
}

async function runWorkbenchRunCommand(runId: string, options: WorkbenchRunOptions): Promise<void> {
  const g = globals();
  try {
    const waitTimeoutMs = options.wait ? workbenchWaitTimeoutMs(options.waitTimeout) : undefined;
    const snapshot = await runWorkbenchCommandEffect(
      Effect.gen(function* () {
        const endpoint = yield* resolveWorkbenchEndpointEffect(options.server);
        const run = options.wait
          ? yield* waitForWorkbenchRunEffect({
              endpoint,
              runId,
              ...(waitTimeoutMs === undefined ? {} : { waitTimeoutMs }),
            })
          : yield* getWorkbenchRunEffect({ endpoint, runId });
        const artifacts = options.artifacts
          ? yield* getWorkbenchRunArtifactsEffect({ endpoint, runId })
          : undefined;
        const events = options.events
          ? yield* getWorkbenchRunEventsEffect({ endpoint, runId })
          : undefined;

        return { run, artifacts, events };
      }),
    );
    if (snapshot === undefined) return;
    const { run, artifacts, events } = snapshot;
    const result =
      artifacts || events
        ? {
            run,
            ...(artifacts ? { artifacts } : {}),
            ...(events ? { events: { object: 'client_event_list', data: events } } : {}),
          }
        : run;
    if (g.json) {
      writeOutput(JSON.stringify(result), g.output);
      return;
    }
    if (events) {
      writeOutput(
        events.map((event) => `${event.seq}  ${event.type}  ${event.createdAt}`).join('\n'),
        g.output,
      );
      return;
    }
    if (artifacts) {
      writeOutput(artifacts.markdown, g.output);
      return;
    }
    writeOutput(
      [
        `Run: ${run.id}`,
        `Status: ${run.status}`,
        `Document: ${run.documentId}`,
        `Parser: ${run.parserId}`,
      ].join('\n'),
      g.output,
    );
  } catch (error) {
    handleWorkbenchCommandError(error, g.json);
  }
}

async function runWorkbenchPiiAnalyzeCommand(
  runId: string,
  options: WorkbenchServerOptions & { read?: boolean },
): Promise<void> {
  const g = globals();
  try {
    const analysis = await runWorkbenchCommandEffect(
      resolveWorkbenchEndpointEffect(options.server).pipe(
        Effect.flatMap((endpoint) =>
          options.read
            ? getWorkbenchPiiAnalysisEffect({ endpoint, runId })
            : analyzeWorkbenchPiiEffect({ endpoint, runId }),
        ),
      ),
    );
    if (analysis === undefined) return;
    if (g.json) {
      writeOutput(JSON.stringify(analysis), g.output);
      return;
    }
    writeOutput(
      [
        `Run: ${analysis.runId}`,
        `Analyzer: ${analysis.engine}`,
        `Candidates: ${analysis.candidates.length}`,
        ...analysis.candidates.map(
          (candidate) =>
            `${candidate.type}  page ${candidate.page}  ${JSON.stringify(candidate.text)}`,
        ),
      ].join('\n'),
      g.output,
    );
  } catch (error) {
    handleWorkbenchCommandError(error, g.json);
  }
}

program
  .command('parse <input>')
  .description('Parse a local PDF through an okra server, or create a hosted document.parse job')
  .summary('Parse a PDF through an okra processing server')
  .option('--server <url>', 'okra.client.v1 processing server URL')
  .option('--parser <parser>', 'Local processing parser id', 'text-layer')
  .option('--model <model>', 'Parser model id')
  .option('--prompt <prompt>', 'Parser prompt id or id@version')
  .option('--engine <engine>', 'Deprecated parser engine alias; use --model/--prompt')
  .option('--strategy <strategy>', 'Parser strategy/vendor alias')
  .option('--wait', 'Wait for the parse job to finish')
  .option('--wait-timeout <seconds>', 'Maximum seconds to wait when --wait is set')
  .action((input: string, options: DocumentParseJobOptions) =>
    runDocumentParseJobCommand(input, options, true),
  );

program
  .command('runs')
  .description('List runs from the selected okra processing server')
  .summary('List processing-server runs')
  .option('--server <url>', 'okra.client.v1 processing server URL')
  .action(runWorkbenchRunsCommand);

program
  .command('run <runId>')
  .description('Read a run from the selected okra processing server')
  .summary('Read processing-server run state')
  .option('--server <url>', 'okra.client.v1 processing server URL')
  .option('--wait', 'Wait until the run reaches a terminal state')
  .option('--wait-timeout <seconds>', 'Maximum seconds to wait when --wait is set')
  .option('--artifacts', 'Include completed markdown, blocks, and manifest')
  .option('--events', "Replay and decode this run's client-protocol SSE events")
  .action(runWorkbenchRunCommand);

const piiCommand = program.command('pii').description('PII analysis operations');

piiCommand
  .command('analyze <runId>')
  .description('Analyze a completed processing-server run for PII')
  .summary('Analyze PII without applying redactions')
  .option('--server <url>', 'okra.client.v1 processing server URL')
  .option('--read', 'Read the persisted analysis without starting a new analysis')
  .action(runWorkbenchPiiAnalyzeCommand);

program
  .command('audit <documentId>')
  .description('Run an audit workflow against an existing document')
  .summary('Run audit workflow')
  .option('--standard <standard>', 'Audit standard', 'wcag')
  .option('--recipe <recipeId>', 'Workflow recipe id to run')
  .option('--attest', 'Request an attestation flip; always requires the live human review gate')
  .action(
    (documentId: string, options: { standard?: string; recipe?: string; attest?: boolean }) => {
      if (options.attest) {
        const g = globals();
        writeHumanReviewRequired('audit', documentId, g);
        return;
      }
      return runDocumentWorkflowCommand('audit', documentId, options);
    },
  );

program
  .command('redact <documentId>')
  .description('Run a redaction workflow against an existing document')
  .summary('Run redaction workflow')
  .option('--model <model>', 'Redaction model or policy capability preference', 'local')
  .option('--policy <json>', 'Redaction policy JSON object')
  .option('--recipe <recipeId>', 'Workflow recipe id to run')
  .action((documentId: string, options: { model?: string; policy?: string; recipe?: string }) =>
    runDocumentWorkflowCommand('redact', documentId, options),
  );

// ── Shared extraction-schema resolution (single-doc `extract` + `collections
//    extract`) so both surfaces handle --content-type and bad schemas identically.

/**
 * Resolve `--content-type <id>` into a schema + grounding flag, mutating
 * `options.schema`/`options.cite` in place. A content type supplies its own
 * schema, so `--content-type` + `--schema` together is a conflict, and an
 * unknown id is `not_found`. No-op when `--content-type` is absent.
 */
function applyContentTypeToOptions(options: {
  schema?: string;
  contentType?: string;
  cite?: boolean;
}): void {
  if (!options.contentType) return;
  if (options.schema) {
    throw new OkraRuntimeError(
      'INVALID_REQUEST',
      'Pass either --content-type or --schema, not both.',
      400,
      {
        error: 'invalid_request',
        message:
          'Pass either --content-type or --schema, not both — a content type already supplies its schema.',
        next_actions: [
          {
            cmd: 'okra content-types show ' + options.contentType,
            why: "Inspect the content type's schema.",
          },
        ],
      },
    );
  }
  const manifest = getContentType(options.contentType);
  if (!manifest) {
    throw new OkraRuntimeError('NOT_FOUND', `No content type "${options.contentType}"`, 404, {
      error: 'not_found',
      message: `No content type "${options.contentType}". Run \`okra content-types list\` to see available content types.`,
      next_actions: [{ cmd: 'okra content-types list', why: 'List the registered content types.' }],
    });
  }
  options.schema = JSON.stringify(manifest.schema);
  if (manifest.evidence.citation_required) options.cite = true;
}

/**
 * Parse a `--schema` arg (file path or inline JSON) into an object, surfacing a
 * stable `invalid_schema` envelope (code 400) on a missing file or bad JSON
 * rather than letting a raw `ENOENT`/`SyntaxError` leak as `error: "error"`.
 */
function loadExtractionSchema(schemaArg: string, cmdHint: string): Record<string, unknown> {
  const inlineSchema = schemaArg.trim().startsWith('{');
  try {
    return JSON.parse(inlineSchema ? schemaArg : readFileSync(schemaArg, 'utf8'));
  } catch (schemaErr) {
    const enoent = (schemaErr as NodeJS.ErrnoException)?.code === 'ENOENT';
    const reason = enoent
      ? `Schema file not found: ${schemaArg}`
      : `Invalid JSON Schema (${inlineSchema ? 'inline' : schemaArg}): ${(schemaErr as Error).message}`;
    throw new OkraRuntimeError('INVALID_REQUEST', reason, 400, {
      error: 'invalid_schema',
      message: reason,
      next_actions: [
        { cmd: cmdHint, why: 'Pass a readable JSON Schema file path or an inline JSON object.' },
      ],
    });
  }
}

// ============================================================================
// extract command — structured extraction from existing doc or upload + extract
// ============================================================================
async function runExtractCommand(
  source: string | undefined,
  options: {
    wait?: boolean;
    schema?: string;
    contentType?: string;
    prompt?: string;
    cite?: boolean;
  },
): Promise<void> {
  const g = globals();
  // Generated content-type nouns (`okra invoice extract …`) invoke this too;
  // globals() infers the command from argv (→ "invoice"), which would label the
  // envelope wrong and skip the `extract` next_actions. Force "extract" so the
  // generated noun is fully equivalent to `extract --content-type <id>`.
  setOutputContext({ json: g.json === true, command: 'extract' });
  try {
    const client = getClient();
    const { readFileSync } = await import('fs');
    const resolvedSource = source;

    // `--content-type <id>` supplies this content type's schema + evidence
    // policy (forces grounding when required); the rest of the flow is unchanged.
    applyContentTypeToOptions(options);

    if (!resolvedSource) {
      throw new OkraRuntimeError(
        'INVALID_REQUEST',
        'Provide a document source: a doc ID, URL, or file path.',
        400,
        {
          error: 'source_required',
          message: 'Provide a document source: a doc ID, URL, or file path.',
          next_actions: [
            {
              cmd: 'okra extract <doc-id> --schema schema.json',
              why: 'Extract from an existing document.',
            },
          ],
        },
      );
    }

    // Resolve doc ID: if source looks like an existing doc, skip upload
    const isExistingDoc = /^(?:ocr|doc)-[A-Za-z0-9_-]+$/.test(resolvedSource);
    let docId: string;

    if (isExistingDoc) {
      docId = resolvedSource;
    } else {
      // Upload first
      const result = await upload(client, resolvedSource, {
        ...g,
        noWait: options.wait === false,
      });
      docId = result.id;

      // If no schema and no-wait, just show upload result
      if (!options.schema || options.wait === false) {
        if (g.json) {
          writeOutput(JSON.stringify(result), g.output);
        } else {
          writeOutput(
            options.wait === false
              ? formatQueuedDocumentMessage(docId)
              : formatDocumentReadyMessage(docId, result.pages),
            g.output,
          );
        }
        return;
      }
    }

    // Schema is required for existing docs, optional for uploads
    if (!options.schema) {
      if (isExistingDoc) {
        throw new OkraRuntimeError(
          'INVALID_REQUEST',
          '--schema is required when extracting from an existing document.',
          400,
          {
            error: 'schema_required',
            message: '--schema is required when extracting from an existing document.',
            next_actions: [
              {
                cmd: 'okra extract <doc-id> --schema schema.json',
                why: 'Provide a JSON Schema (file path or inline) to extract structured data.',
              },
            ],
          },
        );
      }
      return;
    }

    const schemaJson = loadExtractionSchema(
      options.schema,
      'okra extract <doc-id> --schema ./schema.json --cite',
    );

    const prompt = options.prompt || 'Extract all data from this document according to the schema.';

    progress(`Extracting from ${docId}…`, g.quiet);
    const extraction = await client.generate(docId, prompt, {
      schema: schemaJson,
      ...(options.cite ? { cite: true } : {}),
    });

    if (g.json) {
      // `--cite` → the server returns the Anthropic-shaped grounded citation
      // array in meta.citations; surface it verbatim. Default → lean { data }.
      const citations = extraction.meta?.citations ?? [];
      writeOutput(
        JSON.stringify({
          // `document_id` is the canonical id field across the envelope (upload,
          // facet, render, …); `doc_id` stays as a back-compat alias so the
          // `upload → extract` chain reads the same id field name (#592).
          document_id: docId,
          doc_id: docId,
          data: extraction.data ?? extraction.answer,
          ...(citations.length > 0 ? { citations } : {}),
        }),
        g.output,
      );
    } else if (g.output && g.output.endsWith('.csv')) {
      // CSV output for single doc — one header row + one data row
      const data = (extraction.data ?? {}) as Record<string, unknown>;
      const keys = Object.keys(data);
      const header = ['doc_id', ...keys].join(',');
      const values = keys.map((k) => {
        const v = data[k];
        if (v == null) return '';
        if (typeof v === 'string') return `"${String(v).replace(/"/g, '""')}"`;
        return String(v);
      });
      writeOutput([header, [docId, ...values].join(',')].join('\n'), g.output);
    } else {
      writeOutput(JSON.stringify(extraction.data ?? extraction.answer, null, 2), g.output);
    }
  } catch (error) {
    handleError(error, g.json);
  }
}

program
  .command('extract [source]')
  .description('Extract structured data from a document (doc ID, URL, or file path)')
  .summary('Upload a PDF and extract structured data')
  .option('--no-wait', "Fire-and-forget (don't wait for processing)")
  .option('--schema <file>', 'JSON Schema file or inline JSON for structured extraction')
  .option(
    '--content-type <id>',
    'Extract using a registered content type (e.g. invoice) — supplies the schema and grounding',
  )
  .option(
    '--prompt <query>',
    'Extraction prompt (default: "Extract all data according to the schema")',
  )
  .option('--cite', 'Return per-field source citations (page + bbox) for each extracted value')
  .action(runExtractCommand);

// ============================================================================
// render command — A.4.19
// ============================================================================
program
  .command('render <source>')
  .description(
    'Generate a PDF via /v1/renders (ReportLab Python, codemode JS, designed spec, or HTML)',
  )
  .summary('Render a PDF from a script, spec, or HTML')
  .option('-o, --out <file>', 'Output PDF path (use - for stdout)', 'output.pdf')
  .option('--mode <mode>', 'Override auto-detected mode: reportlab | exec | designed | html')
  .option('--input <file>', 'Attach a single input file (basename becomes the script path)')
  .option(
    '--file <name=path>',
    'Additional input file (repeatable)',
    (val: string, acc: string[]) => [...(acc || []), val],
    [] as string[],
  )
  .option('--save', 'Persist into your account as a created document')
  .option('--file-name <name>', 'Override output PDF filename (default: derived from -o)')
  .action(
    async (
      source: string,
      options: {
        out: string;
        mode?: 'reportlab' | 'exec' | 'designed' | 'html';
        input?: string;
        file?: string[];
        save?: boolean;
        fileName?: string;
      },
    ) => {
      const g = globals();
      try {
        const apiKey = getApiKey();
        if (!apiKey) {
          throw createMissingApiKeyError();
        }
        // `-o` is the global `--output` short flag, so `okra render src -o out.pdf`
        // (the documented usage) lands the path in `g.output` while the command's
        // `--out` keeps its `output.pdf` default — the PDF would silently go to
        // `output.pdf` and the result envelope to `out.pdf`. Recover the `-o` path
        // as the PDF destination (the long `--out` form lands in `options.out`
        // directly). When the same path backs both, write the envelope to stdout
        // so it can't clobber the PDF; only send it to a file when it differs.
        const outPath = options.out === 'output.pdf' && g.output ? g.output : options.out;
        const envelopeDest = g.output && g.output !== outPath ? g.output : undefined;
        const result = await render(source, outPath, {
          ...g,
          apiKey,
          baseUrl: getBaseUrl(),
          mode: options.mode,
          input: options.input,
          file: options.file,
          save: options.save,
          fileName: options.fileName,
        });
        // `-`/stdout already streamed the raw PDF bytes; emitting an envelope too
        // would corrupt the stream.
        if (outPath === '-') return;
        if (g.json) {
          writeOutput(JSON.stringify(result), envelopeDest);
        } else {
          const lines = [
            `Rendered: ${result.output_path}`,
            `  mode:       ${result.mode}`,
            `  size:       ${result.size.toLocaleString()} bytes`,
            `  render_id:  ${result.render_id}`,
            `  sha256:     ${result.sha256}`,
          ];
          if (result.document_id) {
            lines.push(`  document:   ${result.document_id}`);
            lines.push(`  download:   ${result.download_url}`);
          }
          writeOutput(lines.join('\n'), envelopeDest);
        }
      } catch (error) {
        handleError(error, g.json);
      }
    },
  );

// ============================================================================
// status command
// ============================================================================
program
  .command('status <docId>', { hidden: true })
  .description('Get document processing status')
  .action(async (docId) => {
    const g = globals();
    try {
      const client = getClient();
      const status = await client.status(docId);
      if (g.json) {
        writeOutput(JSON.stringify(status), g.output);
      } else {
        const lines = [`Document: ${docId}`, `Phase: ${status.phase}`];
        if (typeof status.pagesCompleted === 'number' || typeof status.pagesTotal === 'number') {
          lines.push(`Pages: ${status.pagesCompleted ?? '?'} / ${status.pagesTotal ?? '?'}`);
        }
        writeOutput(lines.join('\n'), g.output);
      }
    } catch (error) {
      handleError(error, g.json);
    }
  });

// ============================================================================
// ask command
// ============================================================================
program
  .command('ask <sourceOrQuestion> [question...]')
  .description(
    'Ask a question from bounded context; legacy --doc asks a processed document (canonical: okra context ask)',
  )
  .summary('Ask about a document')
  .option('--doc <id>', 'Document ID for legacy document chat')
  .option('--model <name>', 'Override model for legacy document chat')
  .option('--stream', 'Stream response tokens as they arrive for legacy document chat')
  .option('--offline <fixture>', 'Use a built-in offline context fixture')
  .option('--source-id <id>', 'Context source id')
  .option(
    '--section <id>',
    'Repeatable context section id',
    (value: string, acc: string[]) => [...acc, value],
    [] as string[],
  )
  .action(
    async (
      sourceOrQuestion: string,
      questionParts: string[] = [],
      options: {
        doc?: string;
        model?: string;
        stream?: boolean;
        offline?: string;
        sourceId?: string;
        section?: string[];
      },
    ) => {
      const g = globals();
      try {
        if (!options.doc) {
          if (options.model || options.stream) {
            throw new OkraRuntimeError(
              'INVALID_REQUEST',
              '--model and --stream require --doc <id> (legacy document chat).',
              400,
              {
                error: 'invalid_request',
                message: '--model and --stream require --doc <id> (legacy document chat).',
                next_actions: [
                  {
                    cmd: 'okra ask <source-id> "<question>"',
                    why: 'Use the context ask surface (no --model/--stream).',
                  },
                ],
              },
            );
          }

          const hasContextSourceOption = !!options.sourceId || !!options.offline;
          const explicitSourceId = questionParts.length ? sourceOrQuestion : undefined;
          const contextQuestionParts = questionParts.length
            ? questionParts
            : hasContextSourceOption
              ? [sourceOrQuestion]
              : [];

          if (!contextQuestionParts.length) {
            throw new OkraRuntimeError(
              'INVALID_REQUEST',
              'Provide a source and a question, or use --doc <id> for legacy document chat.',
              400,
              {
                error: 'invalid_request',
                message:
                  'Provide a source and a question, or use --doc <id> for legacy document chat.',
                next_actions: [
                  {
                    cmd: 'okra ask <source-id> "<question>"',
                    why: 'Ask a cited question about a source.',
                  },
                ],
              },
            );
          }

          await runContextAsk(contextQuestionParts, options, contextDeps, explicitSourceId);
          return;
        }

        const client = getClient();
        const docId = options.doc;
        const question = [sourceOrQuestion, ...questionParts].join(' ');
        const prompt = question;

        if (options.stream) {
          let fullText = '';
          for await (const event of client.stream(docId, prompt, {
            model: options.model,
          })) {
            if (event.type === 'text_delta') {
              fullText += event.text;
              if (!g.json) process.stdout.write(event.text);
            } else if (event.type === 'done') {
              if (!g.json) process.stdout.write('\n');
              if (g.json) {
                writeOutput(JSON.stringify({ docId, question, answer: fullText }), g.output);
              }
            } else if (event.type === 'error') {
              throw new Error(event.message);
            }
          }
          return;
        }

        const result = await client.generate(
          docId,
          prompt,
          options.model ? { model: options.model } : undefined,
        );

        if (g.json) {
          writeOutput(JSON.stringify({ docId, question, ...result }), g.output);
        } else {
          writeOutput(result.answer, g.output);
        }
      } catch (error) {
        handleError(error, g.json);
      }
    },
  );

// ============================================================================
// chat command
// ============================================================================
program
  .command('chat <question>')
  .description('Ask a question about a processed document (canonical: okra context ask)')
  .summary('Ask a question about one document')
  .requiredOption('--doc <id>', 'Document ID')
  .option('--model <name>', 'Override model')
  .option('--stream', 'Stream response tokens as they arrive')
  .action(async (question, options) => {
    const g = globals();
    try {
      const client = getClient();

      if (options.stream) {
        let fullText = '';
        for await (const event of client.stream(options.doc, question, {
          model: options.model,
        })) {
          if (event.type === 'text_delta') {
            fullText += event.text;
            if (!g.json) process.stdout.write(event.text);
          } else if (event.type === 'done') {
            if (!g.json) process.stdout.write('\n');
            if (g.json) {
              writeOutput(
                JSON.stringify({ docId: options.doc, question, answer: fullText }),
                g.output,
              );
            }
          } else if (event.type === 'error') {
            throw new Error(event.message);
          }
        }
        return;
      }

      const result = await client.generate(
        options.doc,
        question,
        options.model ? { model: options.model } : undefined,
      );

      if (g.json) {
        writeOutput(JSON.stringify({ docId: options.doc, question, ...result }), g.output);
      } else {
        writeOutput(result.answer, g.output);
      }
    } catch (error) {
      handleError(error, g.json);
    }
  });

// ============================================================================
// list command
// ============================================================================
program
  .command('list')
  .alias('ls')
  .description('List all documents')
  .summary('List your documents')
  .action(runDocumentListCommand);

// ============================================================================
// read command
// ============================================================================
program
  .command('read <docId>')
  .description('Read processed document markdown')
  .summary('Read document markdown')
  .option('--pages <range>', 'Page range, e.g. 1-5')
  .action(runDocumentReadCommand);

// ============================================================================
// open command
// ============================================================================
program
  .command('open <docId>')
  .description('Print the active profile web-shell URL for a document')
  .summary('Open document shell')
  .option(
    '--view <view>',
    'Initial view: document | graph | parser | audit | redact | review',
    'document',
  )
  .action((docId, options: { view?: string }) => {
    const g = globals();
    try {
      const url = documentShellUrl(docId, options);
      if (g.json) {
        writeOutput(
          JSON.stringify({
            object: 'document_open_url',
            document_id: docId,
            view: options.view ?? 'document',
            url,
          }),
          g.output,
        );
      } else {
        writeOutput(url, g.output);
      }
    } catch (error) {
      handleError(error, g.json);
    }
  });

// ============================================================================
// view command — local PDF-to-HTML streaming
// ============================================================================
program
  .command('view <pdf>')
  .description('Stream a local PDF into an HTML twin using your own provider key')
  .summary('Stream a PDF to local HTML')
  .option('--web', 'Open the local viewer in the default browser')
  .option('--provider <provider>', 'PDF-to-HTML provider', 'unstructured')
  .option('--strategy <strategy>', 'Provider parsing strategy', 'vlm')
  .option('--host <host>', 'Local viewer host', '127.0.0.1')
  .option('--port <port>', 'Local viewer port (0 chooses an available port)', parseLocalViewPort, 0)
  .option(
    '--batch-pages <n>',
    'Pages per provider request',
    (value) => parseLocalViewPositiveInt(value, '--batch-pages'),
    1,
  )
  .option(
    '--concurrency <n>',
    'Concurrent provider requests',
    (value) => parseLocalViewPositiveInt(value, '--concurrency'),
    4,
  )
  .option(
    '--request-timeout <seconds>',
    'Timeout for each provider request',
    (value) => parseLocalViewPositiveInt(value, '--request-timeout'),
    180,
  )
  .option('--max-pages <n>', 'Maximum number of pages to process', (value) =>
    parseLocalViewPositiveInt(value, '--max-pages'),
  )
  .option('--unstructured-url <url>', 'Override the Unstructured API endpoint')
  .action(async (pdf: string, options: LocalViewCommandOptions) => {
    const g = globals();
    try {
      const { runLocalViewCommand } = await import('./commands/view');
      await runLocalViewCommand(pdf, options, { json: g.json, quiet: g.quiet, output: g.output });
    } catch (error) {
      handleError(error, g.json);
    }
  })
  .addHelpText(
    'after',
    [
      '',
      'Environment:',
      '  UNSTRUCTURED_API_KEY  Required provider key (never accepted as an argv flag).',
      '  UNSTRUCTURED_API_URL  Optional HTTPS or loopback endpoint override.',
      '',
      'Data flow: the server and viewer run locally, while PDF page batches are sent',
      'directly to Unstructured. No Okra account, paid API path, or telemetry is used.',
    ].join('\n'),
  );

// ============================================================================
// delete command
// ============================================================================
program
  .command('delete <docId>')
  .alias('rm')
  .description('Delete a document')
  .summary('Delete one document')
  .action(runDocumentDeleteCommand);

// ============================================================================
// collections command
// ============================================================================
const collectionCmd = program
  .command('collections')
  .alias('collection')
  .alias('col')
  .description('Collection operations');
collectionCmd.summary('Query across collections');
collectionCmd.addHelpText('after', COLLECTION_HELP_FOOTER);

collectionCmd
  .command('list')
  .alias('ls')
  .description('List available collections')
  .summary('List collections')
  .action(async () => {
    const g = globals();
    try {
      const client = getClient();
      const rows = await collectionList(client, g);
      writeOutput(formatCollectionList(rows, g.json), g.output);
    } catch (error) {
      handleError(error, g.json);
    }
  });

collectionCmd
  .command('create <name>', { hidden: true })
  .description('Create a new collection')
  .option('--description <text>', 'Collection description')
  .option('--docs <ids>', 'Comma-separated document IDs to seed')
  .action(async (name, options) => {
    const g = globals();
    try {
      const client = getClient();
      const result = await collectionCreate(client, name, { ...g, ...options });
      if (g.json) {
        writeOutput(JSON.stringify(result), g.output);
      } else {
        writeOutput(`Created collection "${result.name}" (${result.id})`, g.output);
      }
    } catch (error) {
      handleError(error, g.json);
    }
  });

collectionCmd
  .command('show <nameOrId>', { hidden: true })
  .description('Show collection details and documents')
  .action(async (nameOrId) => {
    const g = globals();
    try {
      const client = getClient();
      const detail = await collectionShow(client, nameOrId);
      writeOutput(formatCollectionDetail(detail, g.json), g.output);
    } catch (error) {
      handleError(error, g.json);
    }
  });

collectionCmd
  .command('delete <nameOrId>', { hidden: true })
  .alias('rm')
  .description('Delete a collection (documents are preserved)')
  .action(async (nameOrId) => {
    const g = globals();
    try {
      const client = getClient();
      await collectionDelete(client, nameOrId);
      if (g.json) {
        writeOutput(JSON.stringify({ ok: true, deleted: nameOrId }), g.output);
      } else {
        writeOutput(`Deleted collection "${nameOrId}"`, g.output);
      }
    } catch (error) {
      handleError(error, g.json);
    }
  });

collectionCmd
  .command('add <nameOrId> <docIds...>', { hidden: true })
  .description('Add documents to a collection')
  .action(async (nameOrId, docIds) => {
    const g = globals();
    try {
      const client = getClient();
      const result = await collectionAddDocs(client, nameOrId, docIds);
      if (g.json) {
        writeOutput(JSON.stringify(result), g.output);
      } else {
        writeOutput(`Added ${docIds.length} document(s) to "${nameOrId}"`, g.output);
      }
    } catch (error) {
      handleError(error, g.json);
    }
  });

collectionCmd
  .command('remove <nameOrId> <docIds...>', { hidden: true })
  .description('Remove documents from a collection')
  .action(async (nameOrId, docIds) => {
    const g = globals();
    try {
      const client = getClient();
      const result = await collectionRemoveDocs(client, nameOrId, docIds);
      if (g.json) {
        writeOutput(JSON.stringify(result), g.output);
      } else {
        writeOutput(`Removed ${docIds.length} document(s) from "${nameOrId}"`, g.output);
      }
    } catch (error) {
      handleError(error, g.json);
    }
  });

collectionCmd
  .command('query <nameOrId> <question>')
  .description('Fan-out query across collection documents')
  .summary('Ask the same question across a collection')
  .option('--schema <file>', 'Experimental: JSON Schema file for structured extraction')
  .action(async (nameOrId, question, options) => {
    const g = globals();
    try {
      const client = getClient();
      const { results, summary } = await collectionQueryRaw(client, nameOrId, question, {
        ...g,
        schema: options.schema,
      });

      if (g.json) {
        writeOutput(JSON.stringify({ results, summary }), g.output);
      } else if (g.output && g.output.endsWith('.csv')) {
        // -o file.csv → CSV
        writeOutput(formatCollectionCsv(results), g.output);
      } else if (g.output) {
        // -o file.json or other → JSON
        writeOutput(JSON.stringify({ results, summary }), g.output);
      } else {
        // Default: compact table to stdout
        writeOutput(formatCollectionTable(results));
      }

      progress(
        `${summary.completed} completed, ${summary.failed} failed — $${summary.total_cost_usd.toFixed(4)}`,
        g.quiet,
      );
    } catch (error) {
      handleError(error, g.json);
    }
  });

collectionCmd
  .command('extract <nameOrId>', { hidden: true })
  .description('Experimental: extract structured data from all documents in a collection')
  .summary('Experimental structured extraction')
  .option(
    '--schema <file>',
    'Experimental: JSON Schema file or inline JSON for structured extraction',
  )
  .option(
    '--content-type <id>',
    'Extract the whole corpus using a registered content type (e.g. invoice) — supplies the schema and grounding',
  )
  .option('--prompt <query>', 'Extraction prompt (default: auto-generated from schema)')
  .option('--cite', 'Per-field source citations (Anthropic page_location)')
  .action(async (nameOrId, options) => {
    const g = globals();
    try {
      const client = getClient();

      // Parity with single-doc extract: --content-type supplies the schema +
      // grounding; require one of --schema/--content-type; clean schema errors.
      applyContentTypeToOptions(options);
      if (!options.schema) {
        throw new OkraRuntimeError('INVALID_REQUEST', 'Provide --schema or --content-type.', 400, {
          error: 'schema_required',
          message:
            'Provide --schema (a JSON Schema file/inline) or --content-type (a registered content type).',
          next_actions: [
            { cmd: 'okra content-types list', why: 'See the available content types.' },
          ],
        });
      }
      const schemaJson = loadExtractionSchema(
        options.schema,
        'okra collections extract <name> --schema ./schema.json --cite',
      );

      const prompt =
        options.prompt || 'Extract all data from this document according to the schema.';

      const { results, summary } = await collectionQueryRaw(client, nameOrId, prompt, {
        ...g,
        schema: schemaJson,
        cite: options.cite,
      });

      // Use structured formatters that flatten data keys into columns
      if (g.json) {
        writeOutput(JSON.stringify({ results, summary }), g.output);
      } else if (g.output && g.output.endsWith('.csv')) {
        writeOutput(formatExtractCsv(results), g.output);
      } else if (g.output) {
        // Non-csv file output → JSON
        writeOutput(formatExtractJson(results), g.output);
      } else {
        // Default: table to stdout with flattened data columns
        writeOutput(formatExtractTable(results));
      }

      progress(
        `\n${summary.completed} documents — $${summary.total_cost_usd.toFixed(4)} total`,
        g.quiet,
      );
    } catch (error) {
      handleError(error, g.json);
    }
  });

collectionCmd
  .command('publish <nameOrId>', { hidden: true })
  .description('Make a collection publicly queryable')
  .option('--confirm-rights', 'Confirm you own or are licensed to publish this collection publicly')
  .action(async (nameOrId, options: { confirmRights?: boolean }) => {
    const g = globals();
    try {
      const confirmed = await requirePublicPublishRightsConfirmation(
        'collections publish',
        nameOrId,
        g,
        options.confirmRights,
      );
      if (!confirmed) return;

      const client = getClient();
      await collectionSetVisibility(client, nameOrId, 'public');
      if (g.json) {
        writeOutput(
          JSON.stringify({ ok: true, visibility: 'public', collection: nameOrId }),
          g.output,
        );
      } else {
        writeOutput(
          `Published "${nameOrId}"\n` +
            `Share with: okra collections query ${nameOrId} "your question"`,
          g.output,
        );
      }
    } catch (error) {
      handleError(error, g.json);
    }
  });

collectionCmd
  .command('unpublish <nameOrId>', { hidden: true })
  .description('Make a collection private (owner-only)')
  .action(async (nameOrId) => {
    const g = globals();
    try {
      const client = getClient();
      await collectionSetVisibility(client, nameOrId, 'private');
      if (g.json) {
        writeOutput(
          JSON.stringify({ ok: true, visibility: 'private', collection: nameOrId }),
          g.output,
        );
      } else {
        writeOutput(`Unpublished "${nameOrId}" — now private`, g.output);
      }
    } catch (error) {
      handleError(error, g.json);
    }
  });

collectionCmd
  .command('export <nameOrId>', { hidden: true })
  .description('Export pre-computed markdown for all documents in a collection')
  .option('--flat', 'Concatenated markdown with # headers + === separators')
  .option('--zip', 'One markdown file per document in a .zip archive')
  .action(async (nameOrId, options) => {
    const g = globals();
    try {
      const client = getClient();

      if (options.flat && options.zip) {
        throw new Error('Use either --flat or --zip, not both');
      }

      if (options.zip) {
        const bytes = await collectionExport(client, nameOrId, { ...g, zip: true });
        const safeBase =
          String(nameOrId)
            .trim()
            .replace(/[^A-Za-z0-9._-]+/g, '-')
            .replace(/-{2,}/g, '-')
            .replace(/^[-_.]+|[-_.]+$/g, '') || 'collection-export';
        const outputPath = g.output || `${safeBase}.zip`;
        writeFileSync(outputPath, bytes);
        progress(`Wrote → ${outputPath}`, g.quiet);

        if (g.json) {
          writeOutput(
            JSON.stringify({
              format: 'zip',
              file: outputPath,
              bytes: bytes.byteLength,
            }),
          );
        }
        return;
      }

      const result = await collectionExport(client, nameOrId, { ...g, flat: options.flat });

      if (options.flat) {
        writeOutput(formatCollectionExportFlat(result), g.output);
      } else if (g.json) {
        writeOutput(JSON.stringify(result), g.output);
      } else {
        // Default: structured JSON
        writeOutput(JSON.stringify(result, null, 2), g.output);
      }
    } catch (error) {
      handleError(error, g.json);
    }
  });

// ============================================================================
// auth command - Authentication management
// ============================================================================
const authCmd = program.command('auth').description('Manage authentication');

authCmd
  .command('login')
  .description('Save API key to global config')
  .option('-k, --key <apiKey>', 'Set API key non-interactively')
  .action(async (options) => {
    try {
      await authLogin(options.key);
    } catch (error) {
      handleError(error, globals().json);
    }
  });

authCmd
  .command('set-key <apiKey>')
  .description('Save API key to global config (non-interactive)')
  .action(async (apiKey) => {
    try {
      await authSetKey(apiKey);
    } catch (error) {
      handleError(error, globals().json);
    }
  });

authCmd
  .command('status')
  .description('Show authentication status')
  .option('--validate', 'Verify the configured API key with the API')
  .action(async (options: { validate?: boolean }) => {
    try {
      const g = globals();
      await authStatus({ validate: options.validate === true, json: g.json, output: g.output });
    } catch (error) {
      handleError(error, globals().json);
    }
  });

authCmd
  .command('whoami')
  .description('Show current auth identity (alias for status)')
  .action(async () => {
    try {
      const g = globals();
      await authStatus({ validate: true, json: g.json, output: g.output });
    } catch (error) {
      handleError(error, globals().json);
    }
  });

authCmd
  .command('token')
  .description('Print active API key to stdout')
  .action(async () => {
    try {
      await authToken();
    } catch (error) {
      handleError(error, globals().json);
    }
  });

authCmd
  .command('logout')
  .description('Remove API key from global config')
  .action(async () => {
    try {
      await authLogout();
    } catch (error) {
      handleError(error, globals().json);
    }
  });

// ============================================================================
// profile command - Self-host/cloud endpoint profiles
// ============================================================================
const profileCmd = program
  .command('profile')
  .description('Manage cloud and self-host API profiles');

profileCmd
  .command('add <name>')
  .description('Save a cloud or self-host API profile')
  .requiredOption('--base-url <url>', 'API base URL, for example https://my-okra.up.railway.app')
  .option('--api-key <apiKey>', 'Optional API key scoped to this profile')
  .option('--use', 'Immediately make this the active profile')
  .action((name, options) => {
    try {
      profileAdd(name, options);
    } catch (error) {
      handleError(error, globals().json);
    }
  });

profileCmd
  .command('use <name>')
  .description('Switch the active API profile')
  .action((name) => {
    try {
      profileUse(name);
    } catch (error) {
      handleError(error, globals().json);
    }
  });

profileCmd
  .command('current')
  .description('Show the active API profile')
  .action(() => {
    try {
      profileCurrent();
    } catch (error) {
      handleError(error, globals().json);
    }
  });

profileCmd
  .command('list')
  .description('List configured API profiles')
  .action(() => {
    try {
      profileList();
    } catch (error) {
      handleError(error, globals().json);
    }
  });

profileCmd
  .command('remove <name>')
  .description('Remove an API profile')
  .action((name) => {
    try {
      profileRemove(name);
    } catch (error) {
      handleError(error, globals().json);
    }
  });

// ============================================================================
// web / serve commands - one server, browser client optional
// ============================================================================
program
  .command('web [bundleDir]')
  .description('Start the local okra server and open its browser workbench')
  .option('--host <host>', 'Host interface to bind', process.env.HOST ?? '127.0.0.1')
  .option('--port <port>', 'Port to bind', process.env.PORT ?? '8787')
  .option(
    '--public-dir <dir>',
    'Serve a static web app (for example apps/web/dist-node) at / instead of the built-in workbench',
  )
  .option('--no-open', 'Start the web workbench without opening a browser')
  .action(async (bundleDir, options) => {
    await runSelfHostServeCommand(bundleDir, { ...options, open: options.open !== false });
  });

program
  .command('serve [bundleDir]')
  .description('Start the headless self-host runtime (the web client is served at /)')
  .option('--host <host>', 'Host interface to bind', process.env.HOST ?? '127.0.0.1')
  .option('--port <port>', 'Port to bind', process.env.PORT ?? '8787')
  .option(
    '--public-dir <dir>',
    'Serve a static web app (for example apps/web/dist-node) at / instead of the built-in workbench',
  )
  .action(async (bundleDir, options) => {
    await runSelfHostServeCommand(bundleDir, options);
  });

// ============================================================================
// capability command - Isolated self-host capability service entrypoint
// ============================================================================
const capabilityCmd = program
  .command('capability')
  .description('Run isolated self-host capability services');

capabilityCmd
  .command('serve <capabilityId>')
  .description('Serve an okra-capability-http/v1 adapter for one capability')
  .option('--host <host>', 'Host interface to bind', process.env.HOST ?? '0.0.0.0')
  .option(
    '--port <port>',
    'Port to bind',
    process.env.OKRA_CAPABILITY_HTTP_PORT ?? process.env.PORT ?? '8080',
  )
  .action(async (capabilityId, options) => {
    await runCapabilityServeCommand(capabilityId, options);
  });

// ============================================================================
// self-host command - Local self-host bundle validation
// ============================================================================
const selfHostCmd = program
  .command('self-host')
  .description('Validate self-host runtime, recipe, and capability bundles');
selfHostCmd.addHelpText('after', SELF_HOST_HELP_FOOTER);

selfHostCmd
  .command('validate <bundleDir>')
  .description('Validate a self-host runtime bundle without starting a server')
  .action(async (bundleDir) => {
    const g = globals();
    try {
      const result = await Effect.runPromise(
        validateSelfHostBundleEffect(bundleDir).pipe(
          Effect.catch((error) => Effect.fail(error.cause)),
        ),
      );
      writeOutput(formatSelfHostValidationResult(result, g.json), g.output);
      if (!result.ok) process.exit(1);
    } catch (error) {
      handleError(error, g.json);
    }
  });

selfHostCmd
  .command('materialize <bundleDir>')
  .description('Write deterministic Railway/Docker publish artifacts into the bundle')
  .action(async (bundleDir) => {
    const g = globals();
    try {
      const result = await Effect.runPromise(
        materializeSelfHostPublishArtifactsEffect(bundleDir).pipe(
          Effect.catch((error) => Effect.fail(error.cause)),
        ),
      );
      writeOutput(formatSelfHostMaterializeArtifactsResult(result, g.json), g.output);
      if (!result.ok) process.exit(1);
    } catch (error) {
      handleError(error, g.json);
    }
  });

selfHostCmd
  .command('proof <bundleDir>')
  .description('Run no-server draft proof checks against the self-host runtime bundle')
  .option('--evidence-out <path>', 'Write proof JSON to a file')
  .option('--generated-at <value>', 'Override generated_at for deterministic proof artifacts')
  .option(
    '--document-id <documentId>',
    'Document id to use for proof workflow runs',
    'doc-draft-proof',
  )
  .action(
    async (
      bundleDir,
      options: {
        evidenceOut?: string;
        generatedAt?: string;
        documentId?: string;
      },
    ) => {
      const g = globals();
      try {
        const proof = await createSelfHostDraftProof(bundleDir, {
          generatedAt: options.generatedAt,
          documentId: options.documentId,
        });
        if (options.evidenceOut) {
          writeSelfHostDraftProof(options.evidenceOut, proof);
        }
        writeOutput(g.json ? JSON.stringify(proof) : formatSelfHostDraftProof(proof), g.output);
        if (proof.status !== 'passed') process.exit(1);
      } catch (error) {
        handleError(error, g.json);
      }
    },
  );

selfHostCmd
  .command('plan <bundleDir>')
  .description('Render a self-host deploy preflight plan without deploying')
  .option('--target <target>', 'Deploy target to plan for', 'railway')
  .action((bundleDir, options) => {
    const g = globals();
    try {
      const result = createSelfHostDeploymentPlan(bundleDir, options.target);
      writeOutput(formatSelfHostDeploymentPlanResult(result, g.json), g.output);
      if (!result.ok) process.exit(1);
    } catch (error) {
      handleError(error, g.json);
    }
  });

selfHostCmd
  .command('env <bundleDir>')
  .description('Render a self-host .env template without deploying')
  .option('--target <target>', 'Environment target to render for', 'railway')
  .action((bundleDir, options) => {
    const g = globals();
    try {
      const result = createSelfHostEnvArtifact(bundleDir, options.target);
      writeOutput(formatSelfHostEnvArtifactResult(result, g.json), g.output);
      if (!result.ok) process.exit(1);
    } catch (error) {
      handleError(error, g.json);
    }
  });

selfHostCmd
  .command('compose <bundleDir>')
  .description('Render Docker Compose artifacts without starting services')
  .option(
    '--mode <mode>',
    'Compose artifact mode: networks, stack, capabilities, or integrations',
    'networks',
  )
  .action((bundleDir, options) => {
    const g = globals();
    try {
      const result = createSelfHostComposeArtifact(bundleDir, options.mode);
      writeOutput(formatSelfHostComposeArtifactResult(result, g.json), g.output);
      if (!result.ok) process.exit(1);
    } catch (error) {
      handleError(error, g.json);
    }
  });

selfHostCmd
  .command('railway <bundleDir>')
  .description('Render Railway config-as-code for the public app service or a named service')
  .option('--service <serviceId>', 'Railway service id to render config for')
  .action((bundleDir, options: { service?: string }) => {
    const g = globals();
    try {
      const serviceOptionProvided = process.argv.some(
        (arg) => arg === '--service' || arg.startsWith('--service='),
      );
      const result = createSelfHostRailwayConfigArtifact(
        bundleDir,
        serviceOptionProvided ? options.service : undefined,
      );
      writeOutput(formatSelfHostRailwayConfigArtifactResult(result, g.json), g.output);
      if (!result.ok) process.exit(1);
    } catch (error) {
      handleError(error, g.json);
    }
  });

selfHostCmd
  .command('template <bundleDir>')
  .description('Render Railway template composer handoff without publishing')
  .action((bundleDir) => {
    const g = globals();
    try {
      const result = createSelfHostRailwayTemplateArtifact(bundleDir);
      writeOutput(formatSelfHostRailwayTemplateArtifactResult(result, g.json), g.output);
      if (!result.ok) process.exit(1);
    } catch (error) {
      handleError(error, g.json);
    }
  });

selfHostCmd
  .command('template-listing <bundleDir>')
  .description('Render Railway template listing metadata for publication')
  .action((bundleDir) => {
    const g = globals();
    try {
      const result = createSelfHostRailwayTemplateListingArtifact(bundleDir);
      writeOutput(formatSelfHostRailwayTemplateListingArtifactResult(result, g.json), g.output);
      if (!result.ok) process.exit(1);
    } catch (error) {
      handleError(error, g.json);
    }
  });

selfHostCmd
  .command('network-plan <bundleDir>')
  .description('Render Docker network handoff for separated services')
  .action((bundleDir) => {
    const g = globals();
    try {
      const result = createSelfHostDockerNetworkHandoffArtifact(bundleDir);
      writeOutput(formatSelfHostDockerNetworkHandoffArtifactResult(result, g.json), g.output);
      if (!result.ok) process.exit(1);
    } catch (error) {
      handleError(error, g.json);
    }
  });

selfHostCmd
  .command('template-evidence <bundleDir>')
  .description('Render Railway template publication evidence scaffold')
  .action((bundleDir) => {
    const g = globals();
    try {
      const result = createSelfHostRailwayTemplatePublicationEvidenceArtifact(bundleDir);
      writeOutput(
        formatSelfHostRailwayTemplatePublicationEvidenceArtifactResult(result, g.json),
        g.output,
      );
      if (!result.ok) process.exit(1);
    } catch (error) {
      handleError(error, g.json);
    }
  });

selfHostCmd
  .command('implementations <bundleDir>')
  .description('Render capability implementation handoff without publishing images')
  .action((bundleDir) => {
    const g = globals();
    try {
      const result = createSelfHostCapabilityImplementationsArtifact(bundleDir);
      writeOutput(formatSelfHostCapabilityImplementationsArtifactResult(result, g.json), g.output);
      if (!result.ok) process.exit(1);
    } catch (error) {
      handleError(error, g.json);
    }
  });

selfHostCmd
  .command('capability-evidence <bundleDir>')
  .description('Render capability promotion evidence scaffold for production image handoff')
  .action((bundleDir) => {
    const g = globals();
    try {
      const result = createSelfHostCapabilityPromotionEvidenceArtifact(bundleDir);
      writeOutput(
        formatSelfHostCapabilityPromotionEvidenceArtifactResult(result, g.json),
        g.output,
      );
      if (!result.ok) process.exit(1);
    } catch (error) {
      handleError(error, g.json);
    }
  });

selfHostCmd
  .command('evidence-bundle <bundleDir>')
  .description('Render Railway publish evidence bundle scaffold')
  .option('--template-evidence <path>', 'Path to Railway template publication evidence')
  .option('--capability-evidence <path>', 'Path to capability promotion evidence')
  .option('--smoke-evidence <path>', 'Path to live deploy smoke evidence')
  .option('--readiness <path>', 'Path to Railway publish readiness artifact')
  .option('--publish-pack <path>', 'Path to Railway publish pack artifact')
  .action(
    (
      bundleDir,
      options: {
        templateEvidence?: string;
        capabilityEvidence?: string;
        smokeEvidence?: string;
        readiness?: string;
        publishPack?: string;
      },
    ) => {
      const g = globals();
      try {
        const result = createSelfHostRailwayPublishEvidenceBundleArtifact(bundleDir, {
          templateEvidencePath: options.templateEvidence,
          capabilityEvidencePath: options.capabilityEvidence,
          smokeEvidencePath: options.smokeEvidence,
          readinessPath: options.readiness,
          publishPackPath: options.publishPack,
        });
        writeOutput(
          formatSelfHostRailwayPublishEvidenceBundleArtifactResult(result, g.json),
          g.output,
        );
        if (!result.ok) process.exit(1);
      } catch (error) {
        handleError(error, g.json);
      }
    },
  );

selfHostCmd
  .command('publish-pack <bundleDir>')
  .description('Render Railway template publication pack with Docker/network separation')
  .option('--template-url <url>', 'Published Railway template URL, when available')
  .option(
    '--evidence-bundle <path>',
    'JSON publish evidence bundle from `okra self-host evidence-bundle`',
  )
  .option(
    '--template-evidence <path>',
    'JSON Railway template publication evidence from `okra self-host template-evidence`',
  )
  .option('--base-url <url>', 'Deployed self-host base URL, when available')
  .option('--model-backed <capabilityRefs>', 'Comma-separated model-backed capability refs')
  .option(
    '--capability-evidence <path>',
    'JSON capability promotion evidence from `okra self-host capability-evidence`',
  )
  .option(
    '--smoke-evidence <path>',
    'JSON smoke evidence from `okra self-host smoke --evidence-out`',
  )
  .option('--smoke-passed', 'Mark live self-host smoke as passed for the supplied base URL')
  .action(
    (
      bundleDir,
      options: {
        templateUrl?: string;
        evidenceBundle?: string;
        templateEvidence?: string;
        baseUrl?: string;
        modelBacked?: string;
        capabilityEvidence?: string;
        smokeEvidence?: string;
        smokePassed?: boolean;
      },
    ) => {
      const g = globals();
      try {
        const result = createSelfHostRailwayPublishPackArtifact(bundleDir, {
          templateUrl: options.templateUrl,
          evidenceBundlePath: options.evidenceBundle,
          templateEvidencePath: options.templateEvidence,
          deploymentBaseUrl: options.baseUrl,
          modelBackedCapabilities: options.modelBacked
            ?.split(',')
            .map((item) => item.trim())
            .filter(Boolean),
          capabilityEvidencePath: options.capabilityEvidence,
          smokeEvidencePath: options.smokeEvidence,
          smokePassed: options.smokePassed === true,
        });
        writeOutput(formatSelfHostRailwayPublishPackArtifactResult(result, g.json), g.output);
        if (!result.ok) process.exit(1);
      } catch (error) {
        handleError(error, g.json);
      }
    },
  );

selfHostCmd
  .command('readiness <bundleDir>')
  .description('Render Railway publish-readiness gates without publishing')
  .option('--template-url <url>', 'Published Railway template URL, when available')
  .option(
    '--evidence-bundle <path>',
    'JSON publish evidence bundle from `okra self-host evidence-bundle`',
  )
  .option(
    '--template-evidence <path>',
    'JSON Railway template publication evidence from `okra self-host template-evidence`',
  )
  .option('--base-url <url>', 'Deployed self-host base URL, when available')
  .option('--model-backed <capabilityRefs>', 'Comma-separated model-backed capability refs')
  .option(
    '--capability-evidence <path>',
    'JSON capability promotion evidence from `okra self-host capability-evidence`',
  )
  .option(
    '--smoke-evidence <path>',
    'JSON smoke evidence from `okra self-host smoke --evidence-out`',
  )
  .option('--smoke-passed', 'Mark live self-host smoke as passed for the supplied base URL')
  .action(
    (
      bundleDir,
      options: {
        templateUrl?: string;
        evidenceBundle?: string;
        templateEvidence?: string;
        baseUrl?: string;
        modelBacked?: string;
        capabilityEvidence?: string;
        smokeEvidence?: string;
        smokePassed?: boolean;
      },
    ) => {
      const g = globals();
      try {
        const result = createSelfHostRailwayPublishReadinessArtifact(bundleDir, {
          templateUrl: options.templateUrl,
          evidenceBundlePath: options.evidenceBundle,
          templateEvidencePath: options.templateEvidence,
          deploymentBaseUrl: options.baseUrl,
          modelBackedCapabilities: options.modelBacked
            ?.split(',')
            .map((item) => item.trim())
            .filter(Boolean),
          capabilityEvidencePath: options.capabilityEvidence,
          smokeEvidencePath: options.smokeEvidence,
          smokePassed: options.smokePassed === true,
        });
        writeOutput(formatSelfHostRailwayPublishReadinessArtifactResult(result, g.json), g.output);
        if (!result.ok) process.exit(1);
      } catch (error) {
        handleError(error, g.json);
      }
    },
  );

selfHostCmd
  .command('smoke [baseUrl]')
  .description('Run upload, parse, review workflow smoke checks against a self-host base URL')
  .option('--api-key <apiKey>', 'API key for the target self-host instance')
  .option('--document-id <documentId>', 'Document id to use for the smoke upload')
  .option('--workflow <workflow>', 'Workflow branch to smoke: audit, redact, or both', 'audit')
  .option('--timeout-ms <ms>', 'Upload wait timeout in milliseconds', '5000')
  .option('--evidence-out <path>', 'Write smoke evidence JSON for readiness gates')
  .action(
    async (
      baseUrl: string | undefined,
      options: {
        apiKey?: string;
        documentId?: string;
        workflow?: string;
        timeoutMs?: string;
        evidenceOut?: string;
      },
    ) => {
      const g = globals();
      try {
        const apiKey = options.apiKey ?? getApiKey();
        if (!apiKey) {
          throw createMissingApiKeyError();
        }

        const targetBaseUrl = baseUrl ?? getBaseUrl() ?? 'https://api.okrapdf.com';
        const client = new OkraClient({ apiKey, baseUrl: targetBaseUrl });
        const result = await runSelfHostSmoke(client, {
          baseUrl: targetBaseUrl,
          workflow: parseSmokeWorkflow(options.workflow),
          timeoutMs: parsePositiveInt(options.timeoutMs, '--timeout-ms', 5_000),
          ...(options.documentId ? { documentId: options.documentId } : {}),
        });

        if (options.evidenceOut) {
          writeFileSync(
            options.evidenceOut,
            `${JSON.stringify(createSelfHostSmokeEvidence(result), null, 2)}\n`,
          );
        }

        writeOutput(g.json ? JSON.stringify(result) : formatSelfHostSmokeResult(result), g.output);
        if (!result.ok) process.exit(1);
      } catch (error) {
        handleError(error, g.json);
      }
    },
  );

selfHostCmd
  .command('serve [bundleDir]')
  .description('Start the okra server with a validated self-host bundle')
  .option('--host <host>', 'Host interface to bind', process.env.HOST ?? '127.0.0.1')
  .option('--port <port>', 'Port to bind', process.env.PORT ?? '8787')
  .option(
    '--public-dir <dir>',
    'Serve a static web app (for example apps/web/dist-node) at / instead of the built-in workbench',
  )
  .action(async (bundleDir, options) => {
    await runSelfHostServeCommand(bundleDir, options);
  });

// ============================================================================
// find command - jQuery-like entity search
// ============================================================================
program
  .command('find <jobId> <selector>', { hidden: true })
  .description('Find entities using jQuery-like selectors')
  .option('-k, --top-k <n>', 'Limit results', parseInt)
  .option('-c, --min-confidence <n>', 'Minimum confidence (0-1)', parseFloat)
  .option('-p, --pages <range>', 'Page range (e.g., 1-10)')
  .option('--sort <by>', 'Sort by (confidence|page|type)')
  .option('--stats', 'Show aggregate statistics')
  .option('-f, --format <format>', 'Output format (text|json|entities|ids)', 'text')
  .action(async (jobId, selector, options) => {
    const g = globals();
    try {
      const client = getClient();
      const fmt = g.json ? 'json' : options.format;
      const pageRange = options.pages
        ? (options.pages.split('-').map(Number) as [number, number])
        : undefined;

      const result = await find(client, jobId, selector, {
        topK: options.topK,
        minConfidence: options.minConfidence,
        pageRange,
        sortBy: options.sort,
      });

      if (options.stats && fmt === 'text') {
        writeOutput(formatStats(result.stats), g.output);
      } else {
        writeOutput(formatFindOutput(result, fmt, options.stats), g.output);
      }
    } catch (error) {
      handleError(error, g.json);
    }
  });

// ============================================================================
// search command - Full-text search
// ============================================================================
program
  .command('search <jobId> <query>', { hidden: true })
  .description('Search page content')
  .option('-f, --format <format>', 'Output format (text|json)', 'text')
  .action(async (jobId, query, options) => {
    const g = globals();
    try {
      const client = getClient();
      const fmt = g.json ? 'json' : options.format;
      const result = await search(client, jobId, query);
      writeOutput(formatSearchOutput(result, fmt), g.output);
    } catch (error) {
      handleError(error, g.json);
    }
  });

// ============================================================================
// facet command - User-deployed facets (dynamic Workers via Worker Loader)
// ============================================================================
function getFacetCtx(): FacetCliCtx {
  const apiKey = getApiKey();
  if (!apiKey) {
    process.stderr.write(getMissingApiKeyMessage() + '\n');
    process.exit(1);
  }
  const baseUrl = getBaseUrl() ?? 'https://api.okrapdf.com';
  return { apiKey, baseUrl };
}

const facetCmd = program
  .command('facet', { hidden: true })
  .description('Deploy and invoke user facets (dynamic Workers pushed via API)');

facetCmd
  .command('deploy <slug> <file>')
  .description('Deploy or update a facet from a JS file')
  .option('-d, --description <text>', 'Description')
  .action(async (slug: string, file: string, options: { description?: string }) => {
    const g = globals();
    try {
      const result = await deployFacet(getFacetCtx(), slug, file, options.description);
      if (g.json) {
        writeOutput(JSON.stringify(result), g.output);
      } else {
        writeOutput(
          `Deployed: ${result.slug}\n  sha:  ${result.sha}\n  size: ${result.size_bytes} bytes\n\nNext:\n  okra facet invoke ${result.slug} -p '{}'\n`,
          g.output,
        );
      }
    } catch (error) {
      handleError(error, g.json);
    }
  });

facetCmd
  .command('list')
  .description('List deployed facets')
  .action(async () => {
    const g = globals();
    try {
      const items = await listFacets(getFacetCtx());
      if (g.json) {
        writeOutput(JSON.stringify(items), g.output);
      } else {
        writeOutput(formatFacetList(items), g.output);
      }
    } catch (error) {
      handleError(error, g.json);
    }
  });

facetCmd
  .command('info <slug>')
  .description('Show facet metadata')
  .action(async (slug: string) => {
    const g = globals();
    try {
      const info = await getFacetInfo(getFacetCtx(), slug);
      writeOutput(JSON.stringify(info, null, 2), g.output);
    } catch (error) {
      handleError(error, g.json);
    }
  });

facetCmd
  .command('invoke <slug>')
  .description('Invoke a facet with a JSON payload')
  .option('-p, --payload <json>', 'Inline JSON payload', '{}')
  .option('-f, --payload-file <path>', 'Read payload from a JSON file')
  .action(async (slug: string, options: { payload: string; payloadFile?: string }) => {
    const g = globals();
    try {
      const payload = options.payloadFile
        ? parseInlineJsonArg(readFileSync(options.payloadFile, 'utf8'), '--payload-file')
        : parseInlineJsonArg(options.payload, '--payload');
      const result = await invokeFacet(getFacetCtx(), slug, payload);
      if (g.json) {
        writeOutput(JSON.stringify(result), g.output);
      } else {
        const lines = [
          `${result.run_id}  ${result.status}  ${result.duration_ms}ms`,
          '',
          'Output:',
          JSON.stringify(result.output, null, 2),
        ];
        if (result.error) {
          lines.push('', `Error: ${result.error}`);
        }
        writeOutput(lines.join('\n'), g.output);
      }
      if (result.status === 'error') process.exit(2);
    } catch (error) {
      handleError(error, g.json);
    }
  });

facetCmd
  .command('runs <slug>')
  .description('List recent invocations of a facet')
  .option('-n, --limit <num>', 'Max runs to list', '20')
  .action(async (slug: string, options: { limit: string }) => {
    const g = globals();
    try {
      const limit = Number.parseInt(options.limit, 10);
      const runs = await listFacetRuns(getFacetCtx(), slug, Number.isFinite(limit) ? limit : 20);
      if (g.json) {
        writeOutput(JSON.stringify(runs), g.output);
      } else {
        writeOutput(formatRunList(runs), g.output);
      }
    } catch (error) {
      handleError(error, g.json);
    }
  });

facetCmd
  .command('rm <slug>')
  .description('Delete a facet')
  .action(async (slug: string) => {
    const g = globals();
    try {
      const result = await deleteFacetCmd(getFacetCtx(), slug);
      writeOutput(JSON.stringify(result), g.output);
    } catch (error) {
      handleError(error, g.json);
    }
  });

// ============================================================================
// lens command - Document app lenses (dynamic Workers with persisted state)
// ============================================================================
const lensCmd = program
  .command('lens', { hidden: true })
  .description('Deploy and apply document lenses (stateful dynamic document apps)');

lensCmd
  .command('init <file>')
  .description('Write a starter document lens JS file')
  .action(async (file: string) => {
    const g = globals();
    try {
      writeFileSync(file, LENS_STARTER_SOURCE);
      if (g.json) {
        writeOutput(JSON.stringify({ file }), g.output);
      } else {
        writeOutput(`Wrote lens starter: ${file}`, g.output);
      }
    } catch (error) {
      handleError(error, g.json);
    }
  });

lensCmd
  .command('deploy <slug> <file>')
  .description('Deploy or update a document lens from a JS file')
  .option('-d, --description <text>', 'Description')
  .action(async (slug: string, file: string, options: { description?: string }) => {
    const g = globals();
    try {
      const result = await deployLens(getFacetCtx(), slug, file, options.description);
      if (g.json) {
        writeOutput(JSON.stringify(result), g.output);
      } else {
        writeOutput(
          `Deployed lens: ${result.slug}\n  sha:  ${result.sha}\n  size: ${result.size_bytes} bytes\n\nNext:\n  okra lens apply ${result.slug} --doc <document_id> -p '{}'\n`,
          g.output,
        );
      }
    } catch (error) {
      handleError(error, g.json);
    }
  });

lensCmd
  .command('list')
  .description('List deployed document lenses')
  .action(async () => {
    const g = globals();
    try {
      const items = await listLenses(getFacetCtx());
      if (g.json) {
        writeOutput(JSON.stringify(items), g.output);
      } else {
        writeOutput(formatFacetList(items), g.output);
      }
    } catch (error) {
      handleError(error, g.json);
    }
  });

lensCmd
  .command('info <slug>')
  .description('Show lens metadata')
  .action(async (slug: string) => {
    const g = globals();
    try {
      const info = await getLensInfo(getFacetCtx(), slug);
      writeOutput(JSON.stringify(info, null, 2), g.output);
    } catch (error) {
      handleError(error, g.json);
    }
  });

lensCmd
  .command('apply <slug>')
  .description('Apply a lens to a document and persist lens state')
  .requiredOption('--doc <documentId>', 'Document id')
  .option('-p, --payload <json>', 'Inline JSON payload', '{}')
  .option('-f, --payload-file <path>', 'Read payload from a JSON file')
  .option('--cursor <json>', 'Override incremental cursor')
  .option('--state <json>', 'Override previous lens state')
  .option('--reset', 'Clear previous state before applying')
  .action(
    async (
      slug: string,
      options: {
        doc: string;
        payload: string;
        payloadFile?: string;
        cursor?: string;
        state?: string;
        reset?: boolean;
      },
    ) => {
      const g = globals();
      try {
        const payload = options.payloadFile
          ? parseInlineJsonArg(readFileSync(options.payloadFile, 'utf8'), '--payload-file')
          : parseInlineJsonArg(options.payload, '--payload');
        const result = await applyLens(getFacetCtx(), slug, {
          documentId: options.doc,
          payload,
          cursor: options.cursor ? parseInlineJsonArg(options.cursor, '--cursor') : undefined,
          state: options.state ? parseInlineJsonArg(options.state, '--state') : undefined,
          reset: options.reset === true,
        });
        if (g.json) {
          writeOutput(JSON.stringify(result), g.output);
        } else {
          writeOutput(formatLensApply(result), g.output);
        }
        if (result.status === 'error') process.exit(2);
      } catch (error) {
        handleError(error, g.json);
      }
    },
  );

lensCmd
  .command('state <slug>')
  .description('Show persisted lens state for a document')
  .requiredOption('--doc <documentId>', 'Document id')
  .action(async (slug: string, options: { doc: string }) => {
    const g = globals();
    try {
      const state = await getLensState(getFacetCtx(), slug, options.doc);
      writeOutput(JSON.stringify(state, null, 2), g.output);
    } catch (error) {
      handleError(error, g.json);
    }
  });

lensCmd
  .command('runs <slug>')
  .description('List recent lens invocations')
  .option('-n, --limit <num>', 'Max runs to list', '20')
  .action(async (slug: string, options: { limit: string }) => {
    const g = globals();
    try {
      const limit = Number.parseInt(options.limit, 10);
      const runs = await listLensRuns(getFacetCtx(), slug, Number.isFinite(limit) ? limit : 20);
      if (g.json) {
        writeOutput(JSON.stringify(runs), g.output);
      } else {
        writeOutput(formatRunList(runs), g.output);
      }
    } catch (error) {
      handleError(error, g.json);
    }
  });

lensCmd
  .command('rm <slug>')
  .description('Delete a lens')
  .action(async (slug: string) => {
    const g = globals();
    try {
      const result = await deleteLensCmd(getFacetCtx(), slug);
      writeOutput(JSON.stringify(result), g.output);
    } catch (error) {
      handleError(error, g.json);
    }
  });

interface CompletionEntry {
  command: string;
  description: string;
}

function collectCompletionEntries(root: Command, prefix: string[] = []): CompletionEntry[] {
  const entries: CompletionEntry[] = [];
  for (const child of root.commands) {
    const commandPath = [...prefix, child.name()];
    entries.push({
      command: commandPath.join(' '),
      description: child.description() || child.summary() || 'okra command',
    });
    entries.push(...collectCompletionEntries(child, commandPath));
  }
  return entries;
}

function singleQuote(value: string): string {
  return `'${value.replace(/'/g, `'\\''`)}'`;
}

function formatCompletionScript(shell: 'zsh' | 'bash', entries: CompletionEntry[]): string {
  const sorted = [...entries].sort((a, b) => a.command.localeCompare(b.command));
  if (shell === 'zsh') {
    const lines = sorted.map(
      (entry) => `    ${singleQuote(`${entry.command}:${entry.description}`)}`,
    );
    return [
      '#compdef okra',
      '',
      '_okra() {',
      '  local -a commands',
      '  commands=(',
      ...lines,
      '  )',
      "  _describe -t commands 'okra commands' commands",
      '}',
      '',
      '_okra "$@"',
    ].join('\n');
  }

  const words = sorted.map((entry) => entry.command).join('\n');
  return [
    '_okra_completions() {',
    '  local cur',
    '  cur="${COMP_WORDS[COMP_CWORD]}"',
    '  local commands',
    `  commands=${singleQuote(words)}`,
    '  COMPREPLY=( $(compgen -W "$commands" -- "$cur") )',
    '  return 0',
    '}',
    '',
    'complete -F _okra_completions okra',
  ].join('\n');
}

export function generateCompletionScript(shell: 'zsh' | 'bash', root: Command = program): string {
  return formatCompletionScript(shell, collectCompletionEntries(root));
}

program
  .command('completions <shell>')
  .description('Generate shell completion scripts')
  .summary('Generate shell completions')
  .action((shell: string) => {
    const g = globals();
    try {
      if (shell !== 'zsh' && shell !== 'bash') {
        throw new OkraRuntimeError('INVALID_REQUEST', 'Shell must be one of: zsh, bash', 400, {
          error: 'invalid_shell',
          message: 'Shell must be one of: zsh, bash',
          hint: 'Run `okra completions zsh` or `okra completions bash`.',
          retryable: false,
          next_actions: [
            { cmd: 'okra completions zsh', why: 'Generate a zsh completion script.' },
            { cmd: 'okra completions bash', why: 'Generate a bash completion script.' },
          ],
        });
      }
      writeOutput(generateCompletionScript(shell), g.output);
    } catch (error) {
      handleError(error, g.json);
    }
  });

/**
 * Detect JSON-output intent from the raw user args (used at the top-level error
 * boundary, before a command's own globals() runs). Mirrors
 * normalizeGlobalFlags: explicit -j/--json or -o json, else piped stdout.
 */
/** A command and all its descendants (exitOverride is per-command). */
function collectCommandsDeep(cmd: Command): Command[] {
  const out: Command[] = [cmd];
  for (const sub of cmd.commands) out.push(...collectCommandsDeep(sub));
  return out;
}

type ExitCallbackHolder = { _exitCallback?: unknown };

/** Commander "errors" that are actually informational output, not failures. */
const INFORMATIONAL_COMMANDER_CODES = new Set([
  'commander.help',
  'commander.helpDisplayed',
  'commander.version',
]);

function isCommanderError(error: unknown): error is CommanderError {
  return (
    !!error &&
    typeof error === 'object' &&
    typeof (error as { code?: unknown }).code === 'string' &&
    (error as { code: string }).code.startsWith('commander.') &&
    typeof (error as { exitCode?: unknown }).exitCode === 'number'
  );
}

function wantsJsonOutput(userArgs: string[]): boolean {
  if (userArgs.includes('-j') || userArgs.includes('--json')) return true;
  const oi = userArgs.findIndex((a) => a === '-o' || a === '--output');
  if (oi >= 0 && userArgs[oi + 1] === 'json') return true;
  return process.stdout.isTTY !== true;
}

function wantsNativeCommanderOutput(userArgs: string[]): boolean {
  return userArgs.some(
    (arg) => arg === '-h' || arg === '--help' || arg === '-V' || arg === '--version',
  );
}

/**
 * Run the CLI, routing Commander's own usage errors (missing argument, unknown
 * option/command, invalid choice, excess args) through the same JSON envelope
 * the rest of the CLI uses. Without this, a mis-invocation under `--json` writes
 * only a human message to stderr and exits 1, leaving an agent parsing stdout
 * with empty output instead of the documented { ok:false, error, code,
 * next_actions } contract. Help/version are informational (exitCode 0) and pass
 * through untouched. Used by both the direct-execution entry and the published
 * `okra` bin (via index.ts runCli).
 */
export async function runProgram(argv?: string[]): Promise<void> {
  const userArgs = (argv ?? process.argv).slice(2);
  // exitOverride only affects the command it's set on, so apply it to every
  // command (a subcommand's missing-arg would otherwise bypass this catch).
  // Save/restore _exitCallback so runProgram doesn't leak the override onto the
  // shared `program` singleton that other call sites and tests reuse (commander
  // exposes no public unset).
  const commands = collectCommandsDeep(program);
  const savedCallbacks = commands.map((c) => (c as unknown as ExitCallbackHolder)._exitCallback);
  const savedOutputConfigs: OutputConfiguration[] = commands.map((c) => ({
    ...c.configureOutput(),
  }));
  const suppressCommanderOutput =
    wantsJsonOutput(userArgs) && !wantsNativeCommanderOutput(userArgs);
  for (const c of commands) c.exitOverride();
  if (suppressCommanderOutput) {
    for (const c of commands) {
      c.configureOutput({
        ...c.configureOutput(),
        writeOut: () => undefined,
        writeErr: () => undefined,
        outputError: () => undefined,
      });
    }
  }
  try {
    await program.parseAsync(argv ?? process.argv);
  } catch (error) {
    // Duck-type rather than `instanceof CommanderError`: commander is CJS and
    // the thrown error's class identity can differ from the imported symbol
    // across bundler/test boundaries. Commander errors carry a string `code`
    // ("commander.*") and a numeric `exitCode`. (Explicit `return`s after each
    // process.exit keep the control flow correct when exit is stubbed in tests.)
    if (isCommanderError(error)) {
      // Help/version are informational, not failures — commander has already
      // written the text (to stdout for --help/--version, stderr for bare
      // `okra`). Preserve its native exit code and don't emit an error envelope.
      // Note `commander.help` (bare-command help) has exitCode 1, so key off the
      // code, not the exit code.
      if (INFORMATIONAL_COMMANDER_CODES.has(error.code)) {
        process.exit(error.exitCode);
        return;
      }
      const command = inferCommandFromArgv(userArgs);
      if (wantsJsonOutput(userArgs)) {
        setOutputContext({ json: true, command });
        const helpCmd = command ? `okra ${command} --help` : 'okra --help';
        handleError(
          new OkraRuntimeError('INVALID_REQUEST', error.message, 400, {
            error: 'invalid_usage',
            message: error.message,
            next_actions: [
              { cmd: helpCmd, why: 'Show the usage and accepted arguments for this command.' },
            ],
          }),
          true,
        );
        return;
      }
      // Non-JSON: Commander already wrote the message + help to stderr.
      process.exit(error.exitCode || 1);
      return;
    }
    throw error;
  } finally {
    commands.forEach((c, i) => {
      (c as unknown as ExitCallbackHolder)._exitCallback = savedCallbacks[i];
      c.configureOutput(savedOutputConfigs[i] ?? {});
    });
  }
}

if (isDirectExecution()) {
  void runProgram();
}
